import React, {
  useState, useEffect, useCallback, useRef,
} from 'react';
import {
  Box,
  Typography,
  Paper,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Card,
  CardContent,
  CardHeader,
  Chip,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  Alert,
  LinearProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Grid,
  Stack,
  Badge,
  Tooltip,
  useTheme,
  useMediaQuery,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Switch,
  FormControlLabel,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  AlertTitle,
  CircularProgress,
  Fade,
  Zoom,
  Slide,
  Collapse,
  Avatar,
  Slider,
  InputAdornment,
} from '@mui/material';
// Timeline components will be implemented using basic MUI components
import {
  PlayArrow as PlayArrowIcon,
  Stop as StopIcon,
  Pause as PauseIcon,
  Replay as ReplayIcon,
  Refresh as RefreshIcon,
  Visibility as VisibilityIcon,
  ExpandMore as ExpandMoreIcon,
  Search as SearchIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  Schedule as PendingIcon,
  HourglassEmpty as RunningIcon,
  SkipNext as SkipNextIcon,
  Timeline as TimelineIcon,
  Code as CodeIcon,
  Api as ApiIcon,
  SmartToy as AiIcon,
  Description as FormIcon,
  Storage as RagIcon,
  Hub as ParallelIcon,
  CallMerge as JoinIcon,
  PlaylistPlay as StartIcon,
  StopCircle as EndIcon,
  Settings as ConfigIcon,
  BugReport as DebugIcon,
  Delete as DeleteIcon,
  CleaningServices as CleaningServicesIcon,
  Memory as MemoryIcon,
  Speed as PerformanceIcon,
  Psychology as ConditionalIcon,
  Loop as LoopIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  Assessment as AssessmentIcon,
  Monitor as MonitorIcon,
  Tune as TuneIcon,
  PlaylistAdd as AddIcon,
  FilterList as FilterIcon,
  Download as DownloadIcon,
  Upload as ImportIcon,
  RestartAlt as RestartIcon,
  // Additional icons for node types
  Email as EmailIcon,
  Sms as SmsIcon,
  Chat as ChatIcon,
  AccessTime as DelayIcon,
  Build as ToolIcon,
  Webhook as WebhookIcon,
  Person as HumanInputIcon,
  Notifications as NotificationIcon,
  Event as CalendarIcon,
  Phone as VoiceCallIcon,
  Share as SocialPostIcon,
  Queue as MessageQueueIcon,
  TouchApp as TriggerIcon,
  PlayCircle as ResumeIcon,
  PlayCircleFilled as PlayIcon,
} from '@mui/icons-material';
import {
  Timeline,
  TimelineItem,
  TimelineSeparator,
  TimelineDot,
  TimelineConnector,
  TimelineContent,
} from '@mui/lab';
import axios from 'axios';
import axiosClient, { pythonClient } from '../api/axios.client.js';
import {
  getLangGraphCleanupUrl, getLangGraphBackendUrl, getLangGraphFlowStatusUrl, getLangGraphWsUrl,
} from '../utils/langgraphConfig.js';
import {
  executeFlowStreaming,
  recoverFlowExecution,
  getExecutionMetrics,
  getSystemHealth,
} from '../api/agenticFlow.api';
import {
  ErrorRecoveryModal,
  InlineErrorRecovery,
  ErrorBoundaryWithRecovery,
} from '../components/ErrorRecovery';

// Enhanced node type mapping with new node types
const getNodeIcon = (nodeType) => {
  const iconMap = {
    'ai-agent': AiIcon,
    'api-call': ApiIcon,
    'code-executor': CodeIcon,
    form: FormIcon,
    rag: RagIcon,
    parallel: ParallelIcon,
    join: JoinIcon,
    start: StartIcon,
    end: EndIcon,
    conditional: ConditionalIcon,
    transform: ConfigIcon,
    webhook: ApiIcon,
    database: Storage,
    email: FormIcon,
    loop: LoopIcon,
    sms: FormIcon,
    delay: PendingIcon,
    scheduler: PendingIcon,
    'decision-tree': ConditionalIcon,
    switch: ConditionalIcon,
    'try-catch': ErrorIcon,
    filter: ConfigIcon,
    sort: ConfigIcon,
    merge: JoinIcon,
    group: ConfigIcon,
    chat: FormIcon,
    'message-queue': Storage,
    'voice-call': FormIcon,
    trigger: PlayIcon,
    'social-post': FormIcon,
    'mcp-tool': ConfigIcon,
    'human-input': FormIcon,
    policy: ConfigIcon,
    'file-operations': Storage,
    'data-validation': ConfigIcon,
    'web-crawler': ApiIcon,
    connector: ConfigIcon,
    default: ConfigIcon,
  };
  return iconMap[nodeType] || iconMap.default;
};

// Enhanced status color mapping
const getStatusColor = (status) => {
  const colorMap = {
    pending: 'default',
    running: 'primary',
    completed: 'success',
    failed: 'error',
    skipped: 'warning',
    paused: 'warning',
    debugging: 'info',
    cancelled: 'error',
    waiting_for_form: 'info',
    conditional_branch: 'secondary',
    loop_back: 'warning',
  };
  return colorMap[status] || 'default';
};

// Enhanced status icon mapping
const getStatusIcon = (status) => {
  const iconMap = {
    pending: PendingIcon,
    running: RunningIcon,
    completed: SuccessIcon,
    failed: ErrorIcon,
    skipped: SkipNextIcon,
    paused: PauseIcon,
    debugging: DebugIcon,
    cancelled: StopIcon,
    waiting_for_form: FormIcon,
    conditional_branch: ConditionalIcon,
    loop_back: LoopIcon,
  };
  return iconMap[status] || PendingIcon;
};

// Enhanced duration formatting
const formatDuration = (duration) => {
  if (!duration) return 'N/A';
  if (duration < 1) return `${(duration * 1000).toFixed(0)}ms`;
  if (duration < 60) return `${duration.toFixed(2)}s`;
  const minutes = Math.floor(duration / 60);
  const seconds = duration % 60;
  return `${minutes}m ${seconds.toFixed(0)}s`;
};

// Enhanced timestamp formatting
const formatTimestamp = (timestamp) => {
  if (!timestamp) return 'N/A';
  const date = new Date(timestamp * 1000);
  return date.toLocaleString();
};

// Enhanced memory formatting
const formatMemoryUsage = (bytes) => {
  if (!bytes) return 'N/A';
  const mb = bytes / (1024 * 1024);
  return `${mb.toFixed(2)} MB`;
};

// Enhanced performance metrics formatting
const formatPerformanceMetrics = (metrics) => {
  if (!metrics) return null;

  return {
    cpu: metrics.cpu ? `${metrics.cpu.toFixed(1)}%` : 'N/A',
    memory: formatMemoryUsage(metrics.memory),
    executionTime: formatDuration(metrics.execution_time),
    throughput: metrics.throughput ? `${metrics.throughput.toFixed(2)} ops/s` : 'N/A',
  };
};

// State variable change tracking
function StateVariableDisplay({ stateData, nodeId, timestamp }) {
  const [expanded, setExpanded] = useState(false);

  if (!stateData || Object.keys(stateData).length === 0) {
    return (
      <Box sx={{ p: 1, bgcolor: 'grey.50', borderRadius: 1 }}>
        <Typography variant="body2" color="text.secondary">
          No state variables for this node
        </Typography>
      </Box>
    );
  }

  const importantKeys = [
    'ai_response', 'form_data', 'rag_context', 'api_response',
    'waiting_for_form_input', 'form_node_waiting', 'paused_at_node',
    'execution_path', 'nodes_executed', 'conditional_branches_count',
  ];

  const filteredData = Object.entries(stateData).filter(([key, value]) => {
    if (stateChangeFilter === 'important') {
      return importantKeys.includes(key) || (value && typeof value === 'object');
    }
    return true;
  });

  return (
    <Box sx={{ mt: 1 }}>
      <Button
        size="small"
        onClick={() => setExpanded(!expanded)}
        startIcon={expanded ? <ExpandMoreIcon /> : <VisibilityIcon />}
        sx={{ mb: 1 }}
      >
        {expanded ? 'Hide' : 'Show'}
        {' '}
        State Variables (
        {filteredData.length}
        )
      </Button>

      {expanded && (
        <Box sx={{
          maxHeight: 300, overflow: 'auto', bgcolor: 'grey.50', p: 1, borderRadius: 1,
        }}
        >
          {filteredData.map(([key, value]) => (
            <Box
              key={key}
              sx={{
                mb: 1, p: 1, bgcolor: 'white', borderRadius: 1,
              }}
            >
              <Typography variant="subtitle2" color="primary" sx={{ fontWeight: 'bold' }}>
                {key}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  fontFamily: 'monospace',
                  fontSize: '0.8rem',
                  wordBreak: 'break-word',
                  maxHeight: 100,
                  overflow: 'auto',
                }}
              >
                {typeof value === 'object' ? JSON.stringify(value, null, 2) : String(value)}
              </Typography>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
}

function FlowExecutionTracker() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Variable Editor Component
  function VariableEditor({ variables = {}, onSave }) {
    const [editedVariables, setEditedVariables] = useState(variables || {});
    const [newVarName, setNewVarName] = useState('');
    const [newVarValue, setNewVarValue] = useState('');

    // Update editedVariables when variables prop changes
    useEffect(() => {
      setEditedVariables(variables || {});
    }, [variables]);

    const handleVariableChange = (key, value) => {
      try {
        const parsedValue = JSON.parse(value);
        setEditedVariables((prev) => ({ ...prev, [key]: parsedValue }));
      } catch (e) {
        setEditedVariables((prev) => ({ ...prev, [key]: value }));
      }
    };

    const handleAddVariable = () => {
      if (newVarName.trim()) {
        try {
          const parsedValue = JSON.parse(newVarValue || 'null');
          setEditedVariables((prev) => ({ ...prev, [newVarName]: parsedValue }));
          setNewVarName('');
          setNewVarValue('');
        } catch (e) {
          alert('Invalid JSON value');
        }
      }
    };

    const handleRemoveVariable = (key) => {
      setEditedVariables((prev) => {
        const newVars = { ...prev };
        delete newVars[key];
        return newVars;
      });
    };

    const handleSave = () => {
      if (typeof onSave === 'function') {
        onSave(editedVariables);
      } else {
        console.error('onSave prop is not a function:', onSave);
      }
    };

    return (
      <Box>
        <Stack spacing={2}>
          {Object.entries(editedVariables).map(([key, value]) => (
            <Box key={key} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <TextField
                label="Variable Name"
                value={key}
                size="small"
                sx={{ minWidth: 150 }}
                InputProps={{ readOnly: true }}
              />
              <TextField
                label="Value (JSON)"
                value={typeof value === 'object' ? JSON.stringify(value, null, 2) : String(value)}
                onChange={(e) => handleVariableChange(key, e.target.value)}
                size="small"
                fullWidth
                multiline
                rows={typeof value === 'object' ? 3 : 1}
              />
              <IconButton
                color="error"
                onClick={() => handleRemoveVariable(key)}
                size="small"
              >
                <DeleteIcon />
              </IconButton>
            </Box>
          ))}

          {/* Add new variable */}
          <Box sx={{
            display: 'flex', alignItems: 'center', gap: 1, pt: 2, borderTop: 1, borderColor: 'divider',
          }}
          >
            <TextField
              label="New Variable Name"
              value={newVarName}
              onChange={(e) => setNewVarName(e.target.value)}
              size="small"
              sx={{ minWidth: 150 }}
              placeholder="variable_name"
            />
            <TextField
              label="Value (JSON)"
              value={newVarValue}
              onChange={(e) => setNewVarValue(e.target.value)}
              size="small"
              fullWidth
              placeholder='null, "string", 123, {"key": "value"}'
            />
            <IconButton
              color="success"
              onClick={handleAddVariable}
              size="small"
            >
              <AddIcon />
            </IconButton>
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
            <Button
              variant="outlined"
              size="small"
              onClick={() => setEditedVariables(variables)}
            >
              Reset
            </Button>
            <Button
              variant="contained"
              size="small"
              onClick={handleSave}
              color="primary"
            >
              Save Changes
            </Button>
          </Box>
        </Stack>
      </Box>
    );
  }

  // Enhanced state management
  const [executions, setExecutions] = useState([]);
  const [selectedExecution, setSelectedExecution] = useState(null);
  const [executionDetails, setExecutionDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [debugDialogOpen, setDebugDialogOpen] = useState(false);
  const [debugExecution, setDebugExecution] = useState(null);
  const [debugData, setDebugData] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [refreshInterval, setRefreshInterval] = useState(null);
  const [authError, setAuthError] = useState(false);
  const [cleanupLoading, setCleanupLoading] = useState(false);

  // Real-time execution tracking
  const [realTimeMode, setRealTimeMode] = useState(false);
  const [activeExecutions, setActiveExecutions] = useState(new Map());
  const [liveNodeUpdates, setLiveNodeUpdates] = useState(new Map());
  const [executionMetrics, setExecutionMetrics] = useState({});
  const [websocketConnected, setWebsocketConnected] = useState(false);
  const [liveExecutionLogs, setLiveExecutionLogs] = useState([]);
  const [nodePerformanceData, setNodePerformanceData] = useState(new Map());
  const [executionTimeline, setExecutionTimeline] = useState([]);
  const websocketRef = useRef(null);
  const heartbeatRef = useRef(null);

  // New enhanced features
  const [activeTab, setActiveTab] = useState(0);
  const [debugMode, setDebugMode] = useState(false);

  // Execution replay state
  const [replayDialogOpen, setReplayDialogOpen] = useState(false);
  const [currentReplayExecution, setCurrentReplayExecution] = useState(null);
  const [isReplayPlaying, setIsReplayPlaying] = useState(false);
  const [currentReplayStep, setCurrentReplayStep] = useState(0);
  const [replaySpeed, setReplaySpeed] = useState(1);
  const [replayElapsedTime, setReplayElapsedTime] = useState(0);
  const replayTimerRef = useRef(null);

  // Variable inspector state
  const [variableInspectorOpen, setVariableInspectorOpen] = useState(false);
  const [currentInspectedExecution, setCurrentInspectedExecution] = useState(null);
  const [variableSearchTerm, setVariableSearchTerm] = useState('');
  const [selectedVariableCategory, setSelectedVariableCategory] = useState(null);
  const [selectedVariable, setSelectedVariable] = useState(null);
  const [variableCategories, setVariableCategories] = useState(['global', 'local', 'context', 'state']);
  const [filteredVariables, setFilteredVariables] = useState([]);
  const [performanceMode, setPerformanceMode] = useState(false);
  const [conditionalFlowMode, setConditionalFlowMode] = useState(false);
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('timestamp');
  const [sortOrder, setSortOrder] = useState('desc');

  // Debug monitoring state
  const [performanceMetrics, setPerformanceMetrics] = useState(null);
  const [systemStatus, setSystemStatus] = useState(null);
  const [errorLog, setErrorLog] = useState([]);
  const [executionTraces, setExecutionTraces] = useState([]);

  // Conditional flow state
  const [conditionalRoutes, setConditionalRoutes] = useState([]);
  const [loopBackData, setLoopBackData] = useState([]);
  const [flowControlData, setFlowControlData] = useState(null);

  // Enhanced filtering and search
  const [filteredExecutions, setFilteredExecutions] = useState([]);
  const [filterOptions, setFilterOptions] = useState({
    status: 'all',
    nodeType: 'all',
    dateFrom: '',
    dateTo: '',
    duration: 'all',
    dateRange: 'all',
  });

  // State variable tracking
  const [stateVariables, setStateVariables] = useState({});
  const [stateHistory, setStateHistory] = useState([]);
  const [showStateChanges, setShowStateChanges] = useState(true);
  const [stateChangeFilter, setStateChangeFilter] = useState('all');

  // NEW: Enhanced streaming state
  const [streamingConnections, setStreamingConnections] = useState(new Map());
  const [streamingMode, setStreamingMode] = useState(false);
  const [streamingMetrics, setStreamingMetrics] = useState({});
  const [recoveryOptions, setRecoveryOptions] = useState(new Map());
  const [systemHealthData, setSystemHealthData] = useState(null);

  // Error recovery state for FlowExecutionTracker
  const [errorRecoveryState, setErrorRecoveryState] = useState({
    showModal: false,
    selectedExecution: null,
    error: null,
    executionData: null,
    recoveryHistory: new Map(), // Track recovery attempts per execution
    bulkRecoveryMode: false,
    selectedExecutions: new Set(),
  });

  // Enhanced error handling and auto-retry state
  const [errorHandlingState, setErrorHandlingState] = useState({
    autoRetryEnabled: false,
    maxRetryAttempts: 3,
    retryDelay: 5000, // 5 seconds
    errorCategories: new Map(), // Categorize errors by type
    retryQueue: [], // Queue of executions to retry
    isRetrying: false,
  });

  // Check if user is authenticated
  const isAuthenticated = () => {
    const token = localStorage.getItem('tkn');
    return !!token;
  };

  // Helper function to get node icon for real-time display
  const getNodeIcon = (nodeType) => {
    const iconMap = {
      'ai-agent': AiIcon,
      'api-call': ApiIcon,
      'code-executor': CodeIcon,
      database: DatabaseIcon,
      email: EmailIcon,
      sms: SmsIcon,
      chat: ChatIcon,
      form: FormIcon,
      rag: RagIcon,
      parallel: ParallelIcon,
      join: JoinIcon,
      loop: LoopIcon,
      conditional: ConditionalIcon,
      trigger: TriggerIcon,
      scheduler: SchedulerIcon,
      delay: DelayIcon,
      logger: LoggerIcon,
      debugger: DebuggerIcon,
      memory: MemoryIcon,
      tool: ToolIcon,
      webhook: WebhookIcon,
      'human-input': HumanInputIcon,
      notification: NotificationIcon,
      calendar: CalendarIcon,
      'voice-call': VoiceCallIcon,
      'social-post': SocialPostIcon,
      'message-queue': MessageQueueIcon,
      start: StartIcon,
      end: EndIcon,
      'web-crawler': ApiIcon,
      connector: SettingsIcon,
    };
    return iconMap[nodeType] || SettingsIcon;
  };

  // Enhanced error recovery handlers for FlowExecutionTracker
  const handleExecutionError = useCallback((executionId, error, executionData) => {
    console.log('❌ FlowExecutionTracker: Execution error for', executionId, error);

    // Store error details for recovery
    setErrorRecoveryState((prev) => ({
      ...prev,
      selectedExecution: executionId,
      error,
      executionData,
    }));

    // Update execution status to show error
    setActiveExecutions((prev) => prev.map((exec) => (exec.id === executionId
      ? {
        ...exec, status: 'error', error, canRecover: true,
      }
      : exec)));

    // Add to recovery history
    setErrorRecoveryState((prev) => {
      const newHistory = new Map(prev.recoveryHistory);
      const executionHistory = newHistory.get(executionId) || [];
      executionHistory.push({
        timestamp: new Date().toISOString(),
        error,
        type: 'error_detected',
      });
      newHistory.set(executionId, executionHistory);

      return {
        ...prev,
        recoveryHistory: newHistory,
      };
    });
  }, []);

  const handleBulkRecovery = useCallback(async (executionIds) => {
    console.log('🔄 FlowExecutionTracker: Starting bulk recovery for', executionIds.length, 'executions');

    setErrorRecoveryState((prev) => ({
      ...prev,
      bulkRecoveryMode: true,
    }));

    const results = [];

    for (const executionId of executionIds) {
      try {
        const result = await recoverFlowExecution(executionId);
        results.push({ executionId, success: result.success, result });

        if (result.success) {
          // Update execution status
          setActiveExecutions((prev) => prev.map((exec) => (exec.id === executionId
            ? {
              ...exec, status: 'completed', error: null, canRecover: false,
            }
            : exec)));
        }
      } catch (error) {
        results.push({ executionId, success: false, error: error.message });
      }
    }

    setErrorRecoveryState((prev) => ({
      ...prev,
      bulkRecoveryMode: false,
      selectedExecutions: new Set(),
    }));

    // Show results summary
    const successful = results.filter((r) => r.success).length;
    const failed = results.length - successful;

    console.log(`✅ Bulk recovery completed: ${successful} successful, ${failed} failed`);

    return results;
  }, []);

  const handleShowRecoveryModal = useCallback((executionId, error, executionData) => {
    setErrorRecoveryState((prev) => ({
      ...prev,
      showModal: true,
      selectedExecution: executionId,
      error,
      executionData,
    }));
  }, []);

  const handleRecoverySuccess = useCallback((executionId, recoveryResult) => {
    console.log('✅ FlowExecutionTracker: Recovery successful for', executionId);

    // Update execution status
    setActiveExecutions((prev) => prev.map((exec) => (exec.id === executionId
      ? {
        ...exec, status: 'completed', error: null, canRecover: false, recoveredAt: new Date().toISOString(),
      }
      : exec)));

    // Add to recovery history
    setErrorRecoveryState((prev) => {
      const newHistory = new Map(prev.recoveryHistory);
      const executionHistory = newHistory.get(executionId) || [];
      executionHistory.push({
        timestamp: new Date().toISOString(),
        type: 'recovery_success',
        result: recoveryResult,
      });
      newHistory.set(executionId, executionHistory);

      return {
        ...prev,
        showModal: false,
        recoveryHistory: newHistory,
      };
    });
  }, []);

  const handleRetryExecution = useCallback(async (executionId, executionData) => {
    console.log('🔄 FlowExecutionTracker: Retrying execution', executionId);

    // Clear error state
    setActiveExecutions((prev) => prev.map((exec) => (exec.id === executionId
      ? {
        ...exec, status: 'running', error: null, canRecover: false, retryCount: (exec.retryCount || 0) + 1,
      }
      : exec)));

    // Start new execution with same flow data
    if (executionData?.flowData) {
      await startStreamingExecution(executionData.flowData);
    }
  }, []);

  // NEW: Enhanced streaming execution with SSE
  const startStreamingExecution = useCallback(async (flowData) => {
    if (!flowData) return;

    try {
      console.log('🚀 FlowExecutionTracker: Starting streaming execution');
      setStreamingMode(true);

      const streamConnection = await executeFlowStreaming(
        flowData,
        {}, // Empty input
        // onUpdate callback
        (update) => {
          console.log('📡 FlowExecutionTracker streaming update:', update.type, update.node_id);

          // Update active executions with streaming data
          setActiveExecutions((prev) => {
            const newMap = new Map(prev);
            let execution = newMap.get(update.execution_id);

            if (!execution && update.type === 'flow_start') {
              execution = {
                execution_id: update.execution_id,
                flow_id: update.flow_id,
                startTime: Date.now(),
                nodes: new Map(),
                status: 'running',
                streaming: true,
              };
              newMap.set(update.execution_id, execution);
            }

            if (execution && update.node_id) {
              execution.nodes.set(update.node_id, {
                ...update,
                timestamp: Date.now(),
                status: update.type === 'node_complete' ? 'completed'
                  : update.type === 'node_error' ? 'failed' : 'running',
              });
            }

            return newMap;
          });

          // Update streaming metrics
          setStreamingMetrics((prev) => ({
            ...prev,
            [update.execution_id]: {
              progress: update.progress,
              currentNode: update.node_id,
              lastUpdate: Date.now(),
              executionTime: update.execution_time,
            },
          }));

          // Handle completion or error
          if (update.type === 'flow_complete') {
            setActiveExecutions((prev) => {
              const newMap = new Map(prev);
              const execution = newMap.get(update.execution_id);
              if (execution) {
                execution.status = 'completed';
                execution.endTime = Date.now();
              }
              return newMap;
            });
          } else if (update.type === 'flow_error') {
            // Enhanced error handling with recovery options
            setRecoveryOptions((prev) => {
              const newMap = new Map(prev);
              newMap.set(update.execution_id, {
                canRecover: true,
                lastCheckpoint: update.checkpoint,
                errorMessage: update.error,
                timestamp: Date.now(),
              });
              return newMap;
            });

            // Trigger error recovery handler
            handleExecutionError(update.execution_id, update.error, {
              flowData,
              checkpoint: update.checkpoint,
              executionData: update.data,
            });
          }
        },
        // onError callback
        (error) => {
          console.error('❌ FlowExecutionTracker streaming error:', error);
          setStreamingMode(false);

          // Handle streaming connection errors
          handleExecutionError('streaming_connection', error.message, {
            flowData,
            errorType: 'connection_error',
          });
        },
      );

      // Store streaming connection
      setStreamingConnections((prev) => {
        const newMap = new Map(prev);
        newMap.set(streamConnection.executionId, streamConnection);
        return newMap;
      });
    } catch (error) {
      console.error('FlowExecutionTracker streaming setup failed:', error);
      setStreamingMode(false);
    }
  }, []);

  // NEW: Handle flow recovery
  const handleStreamingRecovery = useCallback(async (executionId) => {
    try {
      console.log('🔄 FlowExecutionTracker: Attempting recovery for', executionId);

      const recoveryResult = await recoverFlowExecution(executionId);

      if (recoveryResult.success) {
        console.log('✅ FlowExecutionTracker: Recovery successful');

        // Remove from recovery options
        setRecoveryOptions((prev) => {
          const newMap = new Map(prev);
          newMap.delete(executionId);
          return newMap;
        });

        // Restart streaming if needed
        if (streamingMode) {
          // Note: Would need flow data to restart - this is a placeholder
          console.log('Recovery successful, would restart streaming if flow data available');
        }
      } else {
        console.error('❌ FlowExecutionTracker: Recovery failed:', recoveryResult.error);
      }
    } catch (error) {
      console.error('FlowExecutionTracker recovery error:', error);
    }
  }, [streamingMode]);

  // Enhanced error handling functions
  const categorizeError = useCallback((error) => {
    const errorString = error?.toString().toLowerCase() || '';

    if (errorString.includes('timeout') || errorString.includes('time out')) {
      return 'timeout';
    } if (errorString.includes('network') || errorString.includes('connection')) {
      return 'network';
    } if (errorString.includes('validation') || errorString.includes('invalid')) {
      return 'validation';
    } if (errorString.includes('permission') || errorString.includes('unauthorized')) {
      return 'permission';
    } if (errorString.includes('rate limit') || errorString.includes('too many requests')) {
      return 'rate_limit';
    } if (errorString.includes('not found') || errorString.includes('404')) {
      return 'not_found';
    } if (errorString.includes('server error') || errorString.includes('500')) {
      return 'server_error';
    }
    return 'unknown';
  }, []);

  const shouldAutoRetry = useCallback((error, executionId) => {
    if (!errorHandlingState.autoRetryEnabled) return false;

    const category = categorizeError(error);
    const retryableCategories = ['timeout', 'network', 'rate_limit', 'server_error'];

    if (!retryableCategories.includes(category)) return false;

    // Check retry history
    const history = errorRecoveryState.recoveryHistory.get(executionId) || [];
    return history.length < errorHandlingState.maxRetryAttempts;
  }, [errorHandlingState, errorRecoveryState.recoveryHistory, categorizeError]);

  const addToRetryQueue = useCallback((executionId, error, executionData) => {
    if (shouldAutoRetry(error, executionId)) {
      setErrorHandlingState((prev) => ({
        ...prev,
        retryQueue: [...prev.retryQueue, {
          executionId,
          error,
          executionData,
          attempts: 0,
          nextRetryAt: Date.now() + prev.retryDelay,
        }],
      }));
    }
  }, [shouldAutoRetry]);

  const processRetryQueue = useCallback(async () => {
    if (errorHandlingState.isRetrying || errorHandlingState.retryQueue.length === 0) return;

    setErrorHandlingState((prev) => ({ ...prev, isRetrying: true }));

    const now = Date.now();
    const readyToRetry = errorHandlingState.retryQueue.filter((item) => item.nextRetryAt <= now);

    for (const item of readyToRetry) {
      try {
        console.log(`🔄 Auto-retrying execution ${item.executionId} (attempt ${item.attempts + 1})`);

        // Update retry history
        setErrorRecoveryState((prev) => ({
          ...prev,
          recoveryHistory: new Map(prev.recoveryHistory.set(item.executionId, [
            ...(prev.recoveryHistory.get(item.executionId) || []),
            { timestamp: now, error: item.error, attempt: item.attempts + 1 },
          ])),
        }));

        // Attempt retry
        await handleRetryExecution(item.executionId, item.executionData);

        // Remove from queue on success
        setErrorHandlingState((prev) => ({
          ...prev,
          retryQueue: prev.retryQueue.filter((q) => q.executionId !== item.executionId),
        }));
      } catch (error) {
        console.error(`❌ Auto-retry failed for ${item.executionId}:`, error);

        // Update queue item
        setErrorHandlingState((prev) => ({
          ...prev,
          retryQueue: prev.retryQueue.map((q) => (q.executionId === item.executionId
            ? { ...q, attempts: q.attempts + 1, nextRetryAt: now + prev.retryDelay * (q.attempts + 1) }
            : q)),
        }));
      }
    }

    setErrorHandlingState((prev) => ({ ...prev, isRetrying: false }));
  }, [errorHandlingState, handleRetryExecution]);

  // Auto-retry timer
  useEffect(() => {
    if (errorHandlingState.retryQueue.length > 0) {
      const timer = setInterval(processRetryQueue, 1000);
      return () => clearInterval(timer);
    }
  }, [errorHandlingState.retryQueue.length, processRetryQueue]);

  // NEW: Load system health data
  const loadSystemHealth = useCallback(async () => {
    try {
      const healthData = await getSystemHealth();
      setSystemHealthData(healthData);
    } catch (error) {
      console.error('Error loading system health:', error);
    }
  }, []);

  // WebSocket connection for real-time updates
  const connectWebSocket = useCallback(() => {
    if (!realTimeMode || websocketRef.current) return;

    try {
      const wsUrl = getLangGraphWsUrl('/ws/execution-tracker');
      const ws = new WebSocket(wsUrl);

      ws.onopen = () => {
        console.log('🔌 WebSocket connected for real-time execution tracking');
        setWebsocketConnected(true);

        // Send authentication token
        const token = localStorage.getItem('tkn');
        if (token) {
          ws.send(JSON.stringify({ type: 'auth', token }));
        }

        // Start heartbeat
        heartbeatRef.current = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send(JSON.stringify({ type: 'ping' }));
          }
        }, 30000);

        // NEW: Load system health when connected
        loadSystemHealth();
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          handleWebSocketMessage(data);
        } catch (error) {
          console.error('Error parsing WebSocket message:', error);
        }
      };

      ws.onclose = () => {
        console.log('🔌 WebSocket disconnected');
        setWebsocketConnected(false);
        if (heartbeatRef.current) {
          clearInterval(heartbeatRef.current);
          heartbeatRef.current = null;
        }

        // Attempt to reconnect after 5 seconds
        if (realTimeMode) {
          setTimeout(() => {
            if (realTimeMode && !websocketRef.current) {
              connectWebSocket();
            }
          }, 5000);
        }
      };

      ws.onerror = (error) => {
        console.error('WebSocket error:', error);
        setWebsocketConnected(false);
      };

      websocketRef.current = ws;
    } catch (error) {
      console.error('Failed to connect WebSocket:', error);
    }
  }, [realTimeMode]);

  const disconnectWebSocket = useCallback(() => {
    if (websocketRef.current) {
      websocketRef.current.close();
      websocketRef.current = null;
    }
    if (heartbeatRef.current) {
      clearInterval(heartbeatRef.current);
      heartbeatRef.current = null;
    }
    setWebsocketConnected(false);
  }, []);

  // Handle WebSocket messages
  const handleWebSocketMessage = useCallback((data) => {
    switch (data.type) {
      case 'execution_started':
        setActiveExecutions((prev) => {
          const newMap = new Map(prev);
          newMap.set(data.execution_id, {
            ...data,
            startTime: Date.now(),
            nodes: new Map(),
            status: 'running',
          });
          return newMap;
        });
        break;

      case 'node_started':
        setActiveExecutions((prev) => {
          const newMap = new Map(prev);
          const execution = newMap.get(data.execution_id);
          if (execution) {
            execution.nodes.set(data.node_id, {
              ...data,
              startTime: Date.now(),
              status: 'running',
            });
            // Update execution state variables
            if (data.execution_state) {
              execution.execution_state = data.execution_state;
            }
          }
          return newMap;
        });

        setLiveNodeUpdates((prev) => {
          const newMap = new Map(prev);
          newMap.set(`${data.execution_id}_${data.node_id}`, {
            ...data,
            timestamp: Date.now(),
            type: 'started',
          });
          return newMap;
        });
        break;

      case 'node_completed':
        setActiveExecutions((prev) => {
          const newMap = new Map(prev);
          const execution = newMap.get(data.execution_id);
          if (execution) {
            const node = execution.nodes.get(data.node_id);
            if (node) {
              node.status = 'completed';
              node.endTime = Date.now();
              node.duration = node.endTime - node.startTime;
            }
            // Update execution state variables
            if (data.execution_state) {
              execution.execution_state = data.execution_state;
            }
          }
          return newMap;
        });

        setLiveNodeUpdates((prev) => {
          const newMap = new Map(prev);
          newMap.set(`${data.execution_id}_${data.node_id}`, {
            ...data,
            timestamp: Date.now(),
            type: 'completed',
          });
          return newMap;
        });

        // Update performance data
        setNodePerformanceData((prev) => {
          const newMap = new Map(prev);
          const key = `${data.execution_id}_${data.node_id}`;
          newMap.set(key, {
            nodeId: data.node_id,
            nodeType: data.node_type,
            duration: data.duration || 0,
            timestamp: Date.now(),
            status: 'completed',
          });
          return newMap;
        });
        break;

      case 'node_failed':
        setActiveExecutions((prev) => {
          const newMap = new Map(prev);
          const execution = newMap.get(data.execution_id);
          if (execution) {
            const node = execution.nodes.get(data.node_id);
            if (node) {
              node.status = 'failed';
              node.endTime = Date.now();
              node.duration = node.endTime - node.startTime;
              node.error = data.error;
            }
            // Update execution state variables even on failure
            if (data.execution_state) {
              execution.execution_state = data.execution_state;
            }
          }
          return newMap;
        });

        setLiveNodeUpdates((prev) => {
          const newMap = new Map(prev);
          newMap.set(`${data.execution_id}_${data.node_id}`, {
            ...data,
            timestamp: Date.now(),
            type: 'failed',
          });
          return newMap;
        });
        break;

      case 'execution_completed':
        setActiveExecutions((prev) => {
          const newMap = new Map(prev);
          const execution = newMap.get(data.execution_id);
          if (execution) {
            execution.status = 'completed';
            execution.endTime = Date.now();
            execution.duration = execution.endTime - execution.startTime;
          }
          return newMap;
        });

        // Move to completed executions after a delay
        setTimeout(() => {
          setActiveExecutions((prev) => {
            const newMap = new Map(prev);
            newMap.delete(data.execution_id);
            return newMap;
          });
          // Refresh executions list
          fetchExecutions();
        }, 5000);
        break;

      case 'execution_failed':
        setActiveExecutions((prev) => {
          const newMap = new Map(prev);
          const execution = newMap.get(data.execution_id);
          if (execution) {
            execution.status = 'failed';
            execution.endTime = Date.now();
            execution.duration = execution.endTime - execution.startTime;
            execution.error = data.error;
          }
          return newMap;
        });
        break;

      case 'execution_log':
        setLiveExecutionLogs((prev) => {
          const newLogs = [...prev, {
            ...data,
            timestamp: Date.now(),
          }].slice(-100); // Keep last 100 logs
          return newLogs;
        });
        break;

      case 'execution_metrics':
        setExecutionMetrics((prev) => ({
          ...prev,
          [data.execution_id]: data.metrics,
        }));
        break;

      case 'pong':
        // Heartbeat response
        break;

      default:
        console.log('Unknown WebSocket message type:', data.type);
    }
  }, []);

  // Toggle real-time mode
  const toggleRealTimeMode = useCallback(() => {
    const newMode = !realTimeMode;
    setRealTimeMode(newMode);

    if (newMode) {
      connectWebSocket();
    } else {
      disconnectWebSocket();
      // Clear real-time data
      setActiveExecutions(new Map());
      setLiveNodeUpdates(new Map());
      setLiveExecutionLogs([]);
      setNodePerformanceData(new Map());
    }
  }, [realTimeMode, connectWebSocket, disconnectWebSocket]);

  // Enhanced fetch executions with new backend endpoints
  const fetchExecutions = useCallback(async () => {
    try {
      setLoading(true);
      setAuthError(false);

      // Fetch executions from Python backend with enhanced data (where streaming executions are stored)
      const langgraphUrl = getLangGraphBackendUrl();
      const response = await axios.get(`${langgraphUrl}/flow-executions`);
      let executions = response.data.executions || [];

      // Prioritize initial executions over continuation executions
      // Initial executions start with "start" node, continuations start with other nodes
      // Process executions

      executions = executions.map((execution) => {
        const executedNodes = execution.execution_details?.executed_nodes
                             || execution.result?.execution_path
                             || execution.context?.execution_path
                             || execution.execution_path || [];

        let isInitialExecution = false;
        if (Array.isArray(executedNodes) && executedNodes.length > 0) {
          const firstNode = typeof executedNodes[0] === 'string' ? executedNodes[0] : executedNodes[0]?.node_id;
          isInitialExecution = firstNode && (firstNode === 'start' || firstNode.startsWith('start'));
        }

        return { ...execution, isInitialExecution };
      }).sort((a, b) => {
        // Sort by: initial executions first, then by timestamp (newest first)
        if (a.isInitialExecution && !b.isInitialExecution) return -1;
        if (!a.isInitialExecution && b.isInitialExecution) return 1;
        return new Date(b.created_at || b.startedAt || 0) - new Date(a.created_at || a.startedAt || 0);
      });

      // If debug mode is enabled, fetch additional debug data
      if (debugMode) {
        await fetchDebugData();
      }

      // If performance mode is enabled, fetch performance metrics
      if (performanceMode) {
        await fetchPerformanceMetrics();
      }

      // If conditional flow mode is enabled, fetch conditional flow data
      if (conditionalFlowMode) {
        await fetchConditionalFlowData();
      }

      setExecutions(executions);
    } catch (error) {
      console.error('Error fetching executions:', error);
      if (error.message === 'Access token required') {
        setAuthError(true);
        setExecutions([]);
      }
    } finally {
      setLoading(false);
    }
  }, [debugMode, performanceMode, conditionalFlowMode]);

  // Enhanced fetch execution details with state tracking
  const fetchExecutionDetails = useCallback(async (executionId) => {
    try {
      setLoading(true);

      // Fetch execution details from Python backend with enhanced data
      const langgraphUrl = getLangGraphBackendUrl();
      const response = await axios.get(`${langgraphUrl}/flow-executions/${executionId}`);

      if (response.data && response.data.execution) {
        const details = response.data.execution;

        // Extract state information from context
        const context = details.context || {};
        const stateData = context.essential_state || context.form_data || {};

        // Build state history from execution steps - use the new execution_steps structure
        const executionSteps = details.execution_steps || [];

        const stateHistory = executionSteps.map((step, index) => ({
          nodeId: step.node_id || `node_${index}`,
          nodeType: step.node_type || 'unknown',
          timestamp: step.start_time || (Date.now() - (executionSteps.length - index) * 1000),
          state: step.input_data || {},
          status: step.status || 'completed',
          output: step.output_data || {},
          duration: step.duration || 0,
          error: step.error,
        }));

        setExecutionDetails(details);
        setStateVariables(stateData);
        setStateHistory(stateHistory);
        setDetailsDialogOpen(true);

        // Check if we have state variables in the context
        const stateVariables = Object.keys(context).filter((key) => !key.startsWith('form_')
          && !key.startsWith('execution_')
          && !key.startsWith('flow_')
          && key !== 'waiting_for_form_input'
          && key !== 'form_node_waiting'
          && key !== 'form_data'
          && key !== 'form_message'
          && key !== 'current_form_message'
          && key !== 'execution_settings');
      }
    } catch (error) {
      console.error('Error fetching execution details:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch debug monitoring data
  const fetchDebugData = useCallback(async () => {
    try {
      const langgraphUrl = getLangGraphBackendUrl();

      // Fetch system status
      const statusResponse = await axios.get(`${langgraphUrl}/debug/status`);
      setSystemStatus(statusResponse.data);

      // Fetch execution traces
      const tracesResponse = await axios.get(`${langgraphUrl}/debug/executions`);
      setExecutionTraces(tracesResponse.data.execution_traces || []);

      // Fetch error log
      const errorResponse = await axios.get(`${langgraphUrl}/debug/errors`);
      setErrorLog(errorResponse.data.error_log || []);

      // Fetch performance metrics
      const perfResponse = await axios.get(`${langgraphUrl}/debug/performance`);
      setPerformanceMetrics(perfResponse.data.performance_metrics || []);
    } catch (error) {
      console.error('Error fetching debug data:', error);
    }
  }, []);

  // Fetch performance metrics
  const fetchPerformanceMetrics = useCallback(async () => {
    try {
      const langgraphUrl = getLangGraphBackendUrl();
      const response = await axios.get(`${langgraphUrl}/debug/performance`);
      setPerformanceMetrics(response.data.performance_metrics || []);
    } catch (error) {
      console.error('Error fetching performance metrics:', error);
    }
  }, []);

  // Fetch conditional flow data
  const fetchConditionalFlowData = useCallback(async () => {
    try {
      const langgraphUrl = getLangGraphBackendUrl();

      // Fetch conditional routes for recent executions
      const recentExecutions = executions.slice(0, 5);
      const conditionalData = [];

      for (const execution of recentExecutions) {
        try {
          const response = await axios.post(`${langgraphUrl}/debug/get-conditional-routes`, {
            current_node_id: execution.current_node_id || 'start',
            edges: execution.edges || [],
            state: execution.context || {},
          });

          if (response.data.success) {
            conditionalData.push({
              execution_id: execution.execution_id,
              routes: response.data.routes,
            });
          }
        } catch (error) {
          console.error(`Error fetching conditional routes for ${execution.execution_id}:`, error);
        }
      }

      setConditionalRoutes(conditionalData);
    } catch (error) {
      console.error('Error fetching conditional flow data:', error);
    }
  }, [executions]);

  // Enhanced execution control functions
  const handleLoopBackControl = useCallback(async (executionId, targetNodeId, action = 'loop_back') => {
    try {
      const langgraphUrl = getLangGraphBackendUrl();
      const response = await axios.post(`${langgraphUrl}/debug/loop-back-control`, {
        execution_id: executionId,
        target_node_id: targetNodeId,
        action,
      });

      if (response.data.success) {
        console.log('Loop-back control successful:', response.data);
        await fetchExecutions(); // Refresh data
      }
    } catch (error) {
      console.error('Error controlling loop-back:', error);
    }
  }, [fetchExecutions]);

  const handleConditionalFlowTest = useCallback(async (flowData) => {
    try {
      const langgraphUrl = getLangGraphBackendUrl();
      const response = await axios.post(`${langgraphUrl}/debug/test-conditional-flow`, flowData);

      if (response.data.success) {
        setFlowControlData(response.data);
        console.log('Conditional flow test successful:', response.data);
      }
    } catch (error) {
      console.error('Error testing conditional flow:', error);
    }
  }, []);

  // Enhanced filtering and search
  const applyFilters = useCallback(() => {
    let filtered = executions;

    // Status filter
    if (filterOptions.status !== 'all') {
      filtered = filtered.filter((exec) => exec.status === filterOptions.status);
    }

    // Node type filter
    if (filterOptions.nodeType !== 'all') {
      filtered = filtered.filter((exec) => exec.execution_steps?.some((step) => step.node_type === filterOptions.nodeType));
    }

    // Date range filter
    if (filterOptions.dateRange !== 'all') {
      const now = Date.now();
      const dayMs = 24 * 60 * 60 * 1000;
      const weekMs = 7 * dayMs;
      const monthMs = 30 * dayMs;

      filtered = filtered.filter((exec) => {
        const execTime = new Date(exec.created_at || exec.startedAt).getTime();
        switch (filterOptions.dateRange) {
          case 'today':
            return now - execTime < dayMs;
          case 'week':
            return now - execTime < weekMs;
          case 'month':
            return now - execTime < monthMs;
          default:
            return true;
        }
      });
    }

    // Date range filter
    if (filterOptions.dateFrom || filterOptions.dateTo) {
      filtered = filtered.filter((exec) => {
        const execDate = new Date(exec.created_at || exec.startedAt || exec.timestamp);
        const fromDate = filterOptions.dateFrom ? new Date(filterOptions.dateFrom) : null;
        const toDate = filterOptions.dateTo ? new Date(filterOptions.dateTo) : null;

        if (fromDate && execDate < fromDate) return false;
        if (toDate && execDate > toDate) return false;
        return true;
      });
    }

    // Duration filter
    if (filterOptions.duration !== 'all') {
      filtered = filtered.filter((exec) => {
        const duration = exec.duration || 0;
        switch (filterOptions.duration) {
          case 'fast':
            return duration < 5;
          case 'medium':
            return duration >= 5 && duration < 30;
          case 'slow':
            return duration >= 30;
          default:
            return true;
        }
      });
    }

    // Search query
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter((exec) => exec.execution_id?.toLowerCase().includes(query)
        || exec.flow_name?.toLowerCase().includes(query)
        || exec.status?.toLowerCase().includes(query));
    }

    // Sorting
    filtered.sort((a, b) => {
      let aVal; let
        bVal;

      switch (sortBy) {
        case 'timestamp':
          // Use timestamp field first, then fallback to created_at or startedAt
          aVal = new Date(a.timestamp || a.created_at || a.startedAt).getTime();
          bVal = new Date(b.timestamp || b.created_at || b.startedAt).getTime();
          break;
        case 'duration':
          aVal = a.duration || 0;
          bVal = b.duration || 0;
          break;
        case 'status':
          aVal = a.status || '';
          bVal = b.status || '';
          break;
        default:
          aVal = a[sortBy] || '';
          bVal = b[sortBy] || '';
      }

      if (sortOrder === 'asc') {
        return aVal > bVal ? 1 : -1;
      }
      return aVal < bVal ? 1 : -1;
    });

    setFilteredExecutions(filtered);
  }, [executions, filterOptions, searchQuery, sortBy, sortOrder]);

  // Apply filters when dependencies change
  useEffect(() => {
    applyFilters();
  }, [applyFilters]);

  // Auto-refresh functionality
  useEffect(() => {
    if (autoRefresh) {
      const interval = setInterval(fetchExecutions, 5000); // Refresh every 5 seconds
      setRefreshInterval(interval);
      return () => clearInterval(interval);
    } if (refreshInterval) {
      clearInterval(refreshInterval);
      setRefreshInterval(null);
    }
  }, [autoRefresh, fetchExecutions]);

  useEffect(() => {
    fetchExecutions();
  }, [fetchExecutions]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (event) => {
      // Only handle shortcuts when not in input fields
      if (event.target.tagName === 'INPUT' || event.target.tagName === 'TEXTAREA') {
        return;
      }

      // Ctrl/Cmd + R: Refresh executions
      if ((event.ctrlKey || event.metaKey) && event.key === 'r') {
        event.preventDefault();
        fetchExecutions();
      }

      // Ctrl/Cmd + F: Focus search
      if ((event.ctrlKey || event.metaKey) && event.key === 'f') {
        event.preventDefault();
        const searchInput = document.querySelector('input[placeholder*="Search"]');
        if (searchInput) {
          searchInput.focus();
        }
      }

      // Ctrl/Cmd + T: Toggle auto-retry
      if ((event.ctrlKey || event.metaKey) && event.key === 't') {
        event.preventDefault();
        setErrorHandlingState((prev) => ({
          ...prev,
          autoRetryEnabled: !prev.autoRetryEnabled,
        }));
      }

      // Escape: Close modals
      if (event.key === 'Escape') {
        setDetailsDialogOpen(false);
        setDebugDialogOpen(false);
        setErrorRecoveryState((prev) => ({ ...prev, showModal: false }));
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [fetchExecutions]);

  // WebSocket connection management
  useEffect(() => {
    if (realTimeMode) {
      connectWebSocket();
    } else {
      disconnectWebSocket();
    }

    return () => {
      disconnectWebSocket();
    };
  }, [realTimeMode, connectWebSocket, disconnectWebSocket]);

  // Cleanup executions function - keeps only the most recent N executions
  const handleCleanupExecutions = useCallback(async (keepRecent = 10) => {
    try {
      setCleanupLoading(true);

      // Use LangGraph backend for cleanup endpoint
      const cleanupUrl = getLangGraphCleanupUrl();
      const response = await axios.delete(`${cleanupUrl}?keep_recent=${keepRecent}`);

      if (response.data && response.data.success) {
        console.log('🧹 Cleanup successful:', response.data);
        // Show success message and refresh the list
        alert(`✅ Cleanup successful!\n\nDeleted: ${response.data.deleted_count} executions\nRemaining: ${response.data.remaining_count} most recent executions\nKept: ${keepRecent} most recent executions`);
        // Refresh the executions list
        await fetchExecutions();
      } else {
        throw new Error(response.data?.error || 'Cleanup failed');
      }
    } catch (error) {
      console.error('Error cleaning up executions:', error);
      alert(`❌ Cleanup failed: ${error.message}`);
    } finally {
      setCleanupLoading(false);
    }
  }, [fetchExecutions]);

  const handleViewDetails = (execution) => {
    setSelectedExecution(execution);
    fetchExecutionDetails(execution.execution_id);
  };

  const handleCloseDetails = () => {
    setDetailsDialogOpen(false);
    setExecutionDetails(null);
    setSelectedExecution(null);
  };

  const handleOpenDebug = (execution) => {
    console.log('🐛 Debug: Opening debug interface for execution:', execution);
    console.log('🐛 Debug: Execution ID:', execution.execution_id);
    console.log('🐛 Debug: Execution status:', execution.status);

    if (!execution || !execution.execution_id) {
      console.error('❌ Debug: Invalid execution object');
      return;
    }

    setDebugExecution(execution);
    setDebugDialogOpen(true);
    setDebugData(null); // Reset debug data
    fetchExecutionDebugData(execution.execution_id);
  };

  const handleCloseDebug = () => {
    setDebugDialogOpen(false);
    setDebugExecution(null);
    setDebugData(null);
  };

  const fetchExecutionDebugData = async (executionId) => {
    try {
      // Use pythonClient with proper base URL and auth
      const response = await pythonClient.get(`/flows/status/${executionId}`);
      const { data } = response;

      const debugInfo = {
        execution_id: executionId,
        status: data.status || 'unknown',
        current_state: data.result || {},
        execution_path: data.result?.execution_path || [],
        variables: data.result?.variables || {},
        paused_at_node: data.result?.paused_at_node || null,
        debug_sessions: data.result?.debug_sessions || [],
        breakpoints: data.result?.breakpoints || [],
        logs: data.logs || [],
        node_telemetry: data.nodeTelemetry || [],
      };

      setDebugData(debugInfo);
    } catch (error) {
      console.error('❌ Error fetching debug data:', error);
      console.error('❌ Error details:', error.response?.status, error.response?.data, error.message);
      setDebugData({ error: error.message, status: 'error' });
    }
  };

  const handleResumeExecution = async (executionId) => {
    try {
      await pythonClient.post(`/flows/submit-form/${executionId}`, {
        form_data: { resume_execution: true },
      });
      handleCloseDebug();
      fetchExecutions();
    } catch (error) {
      console.error('Error resuming execution:', error);
    }
  };

  const handleModifyState = async (executionId, newState) => {
    try {
      await pythonClient.post(`/flows/submit-form/${executionId}`, {
        form_data: {
          modify_state: true,
          new_state: newState,
        },
      });
      fetchExecutionDebugData(executionId);
    } catch (error) {
      console.error('Error modifying state:', error);
    }
  };

  const handleStepExecution = async (executionId) => {
    try {
      await pythonClient.post(`/flows/submit-form/${executionId}`, {
        form_data: { step_execution: true },
      });
      handleCloseDebug();
      fetchExecutions();
    } catch (error) {
      console.error('Error stepping execution:', error);
    }
  };

  const handleRunToEnd = async (executionId) => {
    try {
      await pythonClient.post(`/flows/submit-form/${executionId}`, {
        form_data: { run_to_end: true },
      });
      handleCloseDebug();
      fetchExecutions();
    } catch (error) {
      console.error('Error running to end:', error);
    }
  };

  const handleStopExecution = async (executionId) => {
    try {
      await pythonClient.post(`/flows/submit-form/${executionId}`, {
        form_data: { stop_execution: true },
      });
      handleCloseDebug();
      fetchExecutions();
    } catch (error) {
      console.error('Error stopping execution:', error);
    }
  };

  const handleInspectMemory = async (executionId) => {
    try {
      const response = await pythonClient.get(`/debug/memory/${executionId}`);
      console.log('Memory inspection:', response.data);
      alert('Memory inspection completed. Check console for details.');
    } catch (error) {
      console.error('Error inspecting memory:', error);
    }
  };

  const handleViewExecutionGraph = async (executionId) => {
    try {
      const response = await pythonClient.get(`/debug/graph/${executionId}`);
      console.log('Execution graph:', response.data);
      alert('Execution graph generated. Check console for details.');
    } catch (error) {
      console.error('Error viewing execution graph:', error);
    }
  };

  const handleGenerateReport = async (executionId) => {
    try {
      const response = await pythonClient.get(`/debug/report/${executionId}`);
      const blob = new Blob([JSON.stringify(response.data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `debug-report-${executionId}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error generating report:', error);
    }
  };

  const handleExportDebugData = async (executionId) => {
    try {
      const blob = new Blob([JSON.stringify(debugData, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `debug-data-${executionId}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting debug data:', error);
    }
  };

  const handleResetToCheckpoint = async (executionId) => {
    if (confirm('Are you sure you want to reset to the last checkpoint? This will undo recent changes.')) {
      try {
        await pythonClient.post(`/flows/submit-form/${executionId}`, {
          form_data: { reset_to_checkpoint: true },
        });
        fetchExecutionDebugData(executionId);
      } catch (error) {
        console.error('Error resetting to checkpoint:', error);
      }
    }
  };

  const handleAddBreakpoint = async (executionId) => {
    const nodeId = prompt('Enter node ID to add breakpoint:');
    if (nodeId) {
      try {
        await pythonClient.post(`/flows/submit-form/${executionId}`, {
          form_data: {
            add_breakpoint: true,
            node_id: nodeId,
          },
        });
        alert('Breakpoint added successfully');
      } catch (error) {
        console.error('Error adding breakpoint:', error);
      }
    }
  };

  const handleClearBreakpoints = async (executionId) => {
    if (confirm('Are you sure you want to clear all breakpoints?')) {
      try {
        await pythonClient.post(`/flows/submit-form/${executionId}`, {
          form_data: { clear_breakpoints: true },
        });
        alert('All breakpoints cleared');
        fetchExecutionDebugData(executionId);
      } catch (error) {
        console.error('Error clearing breakpoints:', error);
      }
    }
  };

  const handleRemoveBreakpoint = async (executionId, nodeId) => {
    if (confirm(`Remove breakpoint at node ${nodeId}?`)) {
      try {
        await pythonClient.post(`/flows/submit-form/${executionId}`, {
          form_data: {
            remove_breakpoint: true,
            node_id: nodeId,
          },
        });
        fetchExecutionDebugData(executionId);
      } catch (error) {
        console.error('Error removing breakpoint:', error);
      }
    }
  };

  // Function to compare state variables between consecutive nodes
  const getStateVariableChanges = (currentStep, previousStep) => {
    if (!previousStep) return { added: [], modified: [], removed: [] };

    const currentState = currentStep.metadata?.state_snapshot || currentStep.metadata || {};
    const previousState = previousStep.metadata?.state_snapshot || previousStep.metadata || {};

    const currentKeys = new Set(Object.keys(currentState));
    const previousKeys = new Set(Object.keys(previousState));

    const added = Array.from(currentKeys).filter((key) => !previousKeys.has(key));
    const removed = Array.from(previousKeys).filter((key) => !currentKeys.has(key));
    const modified = Array.from(currentKeys).filter((key) => previousKeys.has(key)
      && JSON.stringify(currentState[key]) !== JSON.stringify(previousState[key]));

    return { added, modified, removed };
  };

  // Function to render execution steps in Stepper format for details dialog
  const renderExecutionStepForStepper = (step, index) => {
    const nodeType = step.node_type || step.nodeType || 'unknown';
    const nodeId = step.node_id || step.nodeId || 'unknown';
    const status = step.status || 'pending';
    const duration = step.duration || (step.durationMs ? step.durationMs / 1000 : null);
    const startTime = step.start_time || (step.startedAt ? new Date(step.startedAt).getTime() / 1000 : null);
    const endTime = step.end_time || (step.finishedAt ? new Date(step.finishedAt).getTime() / 1000 : null);
    const { error } = step;

    // Enhanced data extraction for flow progression
    const inputData = step.input_data || {};
    const outputData = step.output_data || {};
    const stateSnapshot = step.metadata?.state_snapshot || step.metadata || {};
    const context = step.context || {};
    const result = step.result || {};

    // Get state variable changes from previous node
    const previousStep = executionDetails?.execution_steps?.[index - 1];
    const stateChanges = getStateVariableChanges(step, previousStep);

    // Extract state variables (filter out system variables)
    const stateVariables = Object.entries(stateSnapshot).filter(([key]) => !key.startsWith('form_')
      && !key.startsWith('execution_')
      && !key.startsWith('flow_')
      && key !== 'waiting_for_form_input'
      && key !== 'form_node_waiting'
      && key !== 'form_data'
      && key !== 'form_message'
      && key !== 'current_form_message'
      && key !== 'execution_settings');

    // Extract context state variables
    const contextStateVars = Object.entries(context).filter(([key]) => !key.startsWith('form_')
      && !key.startsWith('execution_')
      && !key.startsWith('flow_')
      && key !== 'waiting_for_form_input'
      && key !== 'form_node_waiting'
      && key !== 'form_data'
      && key !== 'form_message'
      && key !== 'current_form_message'
      && key !== 'execution_settings');

    // Combine all state variables
    const allStateVars = [...stateVariables, ...contextStateVars];

    const NodeIcon = getNodeIcon(nodeType);
    const StatusIcon = getStatusIcon(status);
    const statusColor = getStatusColor(status);

    return (
      <Step key={step.step_id || index} active={step.status === 'running'} completed={step.status === 'completed'}>
        <StepLabel
          icon={(
            <Badge
              badgeContent={index + 1}
              color="primary"
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
              <StatusIcon color={statusColor} />
            </Badge>
          )}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <NodeIcon fontSize="small" />
            <Typography variant="subtitle2">
              {nodeType.replace('-', ' ').toUpperCase()}
            </Typography>
            <Chip
              label={nodeId}
              size="small"
              variant="outlined"
              sx={{ fontSize: '0.7rem' }}
            />
            {duration && (
              <Chip
                label={formatDuration(duration)}
                size="small"
                color="primary"
                variant="outlined"
                sx={{ fontSize: '0.6rem' }}
              />
            )}
          </Box>
        </StepLabel>
        <StepContent>
          <Card variant="outlined" sx={{ mb: 2 }}>
            <CardContent>
              <Grid container spacing={2}>
                {/* Basic Info */}
                <Grid item xs={12} md={3}>
                  <Typography variant="h6" gutterBottom>Basic Info</Typography>
                  <Stack spacing={1}>
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Status:
                        {' '}
                        <Chip label={status} color={statusColor} size="small" />
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Duration:
                        {' '}
                        {formatDuration(duration)}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Started:
                        {' '}
                        {formatTimestamp(startTime)}
                      </Typography>
                    </Box>
                    {endTime && (
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          Ended:
                          {' '}
                          {formatTimestamp(endTime)}
                        </Typography>
                      </Box>
                    )}
                  </Stack>
                </Grid>

                {/* Error Information */}
                {error && (
                  <Grid item xs={12} md={3}>
                    <Typography variant="h6" gutterBottom>Error Information</Typography>
                    <Alert severity="error" sx={{ fontSize: '0.8rem' }}>
                      <Typography variant="body2" fontWeight="bold">Error:</Typography>
                      <Typography variant="body2">{error}</Typography>
                    </Alert>
                  </Grid>
                )}

                {/* Input Data */}
                <Grid item xs={12} md={3}>
                  <Typography variant="h6" gutterBottom>Input Data</Typography>
                  {Object.keys(inputData).length > 0 ? (
                    <Accordion>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography variant="body2">
                          Input (
                          {Object.keys(inputData).length}
                          {' '}
                          items)
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        <pre style={{ fontSize: '0.8rem', overflow: 'auto', maxHeight: '200px' }}>
                          {JSON.stringify(inputData, null, 2)}
                        </pre>
                      </AccordionDetails>
                    </Accordion>
                  ) : (
                    <Typography variant="body2" color="text.secondary">No input data</Typography>
                  )}
                </Grid>

                {/* Output Data */}
                <Grid item xs={12} md={3}>
                  <Typography variant="h6" gutterBottom>Output Data</Typography>
                  {Object.keys(outputData).length > 0 ? (
                    <Accordion>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography variant="body2">
                          Output (
                          {Object.keys(outputData).length}
                          {' '}
                          items)
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        <pre style={{ fontSize: '0.8rem', overflow: 'auto', maxHeight: '200px' }}>
                          {JSON.stringify(outputData, null, 2)}
                        </pre>
                      </AccordionDetails>
                    </Accordion>
                  ) : (
                    <Typography variant="body2" color="text.secondary">No output data</Typography>
                  )}
                </Grid>

                {/* State Variable Changes */}
                {(stateChanges.added.length > 0 || stateChanges.modified.length > 0 || stateChanges.removed.length > 0) && (
                  <Grid item xs={12}>
                    <Typography variant="h6" gutterBottom>
                      State Variable Changes from Previous Node
                    </Typography>
                    <Grid container spacing={2}>
                      {stateChanges.added.length > 0 && (
                        <Grid item xs={12} md={4}>
                          <Alert severity="success" sx={{ fontSize: '0.8rem' }}>
                            <Typography variant="body2" fontWeight="bold">
                              Added Variables (
                              {stateChanges.added.length}
                              )
                            </Typography>
                            <Stack spacing={0.5} sx={{ mt: 1 }}>
                              {stateChanges.added.map((key) => (
                                <Typography key={key} variant="body2" fontFamily="monospace" fontSize="0.7rem">
                                  +
                                  {' '}
                                  {key}
                                </Typography>
                              ))}
                            </Stack>
                          </Alert>
                        </Grid>
                      )}
                      {stateChanges.modified.length > 0 && (
                        <Grid item xs={12} md={4}>
                          <Alert severity="warning" sx={{ fontSize: '0.8rem' }}>
                            <Typography variant="body2" fontWeight="bold">
                              Modified Variables (
                              {stateChanges.modified.length}
                              )
                            </Typography>
                            <Stack spacing={0.5} sx={{ mt: 1 }}>
                              {stateChanges.modified.map((key) => (
                                <Typography key={key} variant="body2" fontFamily="monospace" fontSize="0.7rem">
                                  ~
                                  {' '}
                                  {key}
                                </Typography>
                              ))}
                            </Stack>
                          </Alert>
                        </Grid>
                      )}
                      {stateChanges.removed.length > 0 && (
                        <Grid item xs={12} md={4}>
                          <Alert severity="error" sx={{ fontSize: '0.8rem' }}>
                            <Typography variant="body2" fontWeight="bold">
                              Removed Variables (
                              {stateChanges.removed.length}
                              )
                            </Typography>
                            <Stack spacing={0.5} sx={{ mt: 1 }}>
                              {stateChanges.removed.map((key) => (
                                <Typography key={key} variant="body2" fontFamily="monospace" fontSize="0.7rem">
                                  -
                                  {' '}
                                  {key}
                                </Typography>
                              ))}
                            </Stack>
                          </Alert>
                        </Grid>
                      )}
                    </Grid>
                  </Grid>
                )}

                {/* State Variables at this Node */}
                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom>
                    State Variables at Node
                    {' '}
                    {index + 1}
                    {' '}
                    (
                    {allStateVars.length}
                    {' '}
                    variables)
                  </Typography>
                  {allStateVars.length > 0 ? (
                    <TableContainer component={Paper} variant="outlined">
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Variable Name</TableCell>
                            <TableCell>Value</TableCell>
                            <TableCell>Type</TableCell>
                            <TableCell>Source</TableCell>
                            <TableCell>Change</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {allStateVars.map(([key, value]) => {
                            const isAdded = stateChanges.added.includes(key);
                            const isModified = stateChanges.modified.includes(key);
                            const isRemoved = stateChanges.removed.includes(key);

                            return (
                              <TableRow key={key}>
                                <TableCell>
                                  <Typography variant="body2" fontFamily="monospace" fontWeight="bold">
                                    {key}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      fontFamily: 'monospace',
                                      fontSize: '0.75rem',
                                      maxWidth: '300px',
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                    }}
                                  >
                                    {typeof value === 'object'
                                      ? JSON.stringify(value).substring(0, 100) + (JSON.stringify(value).length > 100 ? '...' : '')
                                      : String(value)}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Chip label={typeof value} size="small" variant="outlined" />
                                </TableCell>
                                <TableCell>
                                  <Chip
                                    label={stateSnapshot[key] !== undefined ? 'State' : 'Context'}
                                    size="small"
                                    color={stateSnapshot[key] !== undefined ? 'primary' : 'secondary'}
                                  />
                                </TableCell>
                                <TableCell>
                                  {isAdded && <Chip label="Added" size="small" color="success" />}
                                  {isModified && <Chip label="Modified" size="small" color="warning" />}
                                  {isRemoved && <Chip label="Removed" size="small" color="error" />}
                                  {!isAdded && !isModified && !isRemoved && (
                                    <Chip label="Unchanged" size="small" color="default" variant="outlined" />
                                  )}
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      No state variables available at this node
                    </Typography>
                  )}
                </Grid>

                {/* Node Result */}
                {Object.keys(result).length > 0 && (
                  <Grid item xs={12}>
                    <Typography variant="h6" gutterBottom>Node Result</Typography>
                    <Accordion>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography variant="body2">Result Data</Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        <pre style={{ fontSize: '0.8rem', overflow: 'auto', maxHeight: '200px' }}>
                          {JSON.stringify(result, null, 2)}
                        </pre>
                      </AccordionDetails>
                    </Accordion>
                  </Grid>
                )}

                {/* Flow Progression Info */}
                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom>Flow Progression</Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={6} md={3}>
                      <Typography variant="caption" color="text.secondary">Node Type</Typography>
                      <Typography variant="body2" fontWeight="bold">
                        {nodeType.charAt(0).toUpperCase() + nodeType.slice(1)}
                      </Typography>
                    </Grid>
                    <Grid item xs={6} md={3}>
                      <Typography variant="caption" color="text.secondary">Node ID</Typography>
                      <Typography variant="body2" fontFamily="monospace">
                        {nodeId}
                      </Typography>
                    </Grid>
                    <Grid item xs={6} md={3}>
                      <Typography variant="caption" color="text.secondary">Step Number</Typography>
                      <Typography variant="body2" fontWeight="bold">
                        {index + 1}
                      </Typography>
                    </Grid>
                    <Grid item xs={6} md={3}>
                      <Typography variant="caption" color="text.secondary">Execution Time</Typography>
                      <Typography variant="body2">
                        {formatDuration(duration)}
                      </Typography>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </StepContent>
      </Step>
    );
  };

  const renderExecutionCard = (execution) => {
    // Use new execution_details structure if available
    const executionDetails = execution.execution_details || {};
    const resultDetails = execution.result?.execution_details || {};
    const executedNodes = resultDetails.executed_nodes
      || executionDetails.executed_nodes
      || execution.result?.execution_path
      || execution.context?.execution_path
      || execution.execution_path || [];
    const executionPath = resultDetails.execution_path
      || executionDetails.execution_path
      || execution.result?.execution_path
      || execution.context?.execution_path
      || execution.execution_path || [];

    // Convert execution_log to execution_steps format for compatibility
    const nodesExecuted = execution.nodes_executed || execution.context?.nodes_executed || 0;

    // Use executed_nodes if available, otherwise fall back to parsing execution_log
    let executionSteps;
    if (executedNodes.length > 0) {
      // Handle Node.js server format where executed_nodes is an array of node IDs
      if (typeof executedNodes[0] === 'string') {
        executionSteps = executedNodes.map((nodeId, index) => {
          // Handle both string and object formats
          const actualNodeId = typeof nodeId === 'string' ? nodeId : nodeId.node_id;
          const actualStatus = typeof nodeId === 'string' ? 'completed' : (nodeId.status || 'completed');

          let nodeType = 'unknown';
          if (actualNodeId) {
            if (actualNodeId === 'start') {
              nodeType = 'start';
            } else if (actualNodeId === 'end') {
              nodeType = 'end';
            } else {
              // Extract node type from node ID (e.g., "rag_123" -> "rag")
              nodeType = actualNodeId.split('_')[0] || 'unknown';
            }
          }

          // Determine status based on node type and execution context
          let status = actualStatus;
          let duration = 1000; // Default 1 second

          // If this is the last node, check if execution might still be running
          if (index === executedNodes.length - 1) {
            // Check if flow appears to be completed based on status
            const executionCompleted = execution.status === 'completed' || execution.status === 'complete';
            if (!executionCompleted) {
              status = 'running';
              duration = (execution.execution_time || 1000) * 1000; // Convert to milliseconds
            } else {
              duration = (execution.execution_time || 1000) * 1000 / executedNodes.length;
            }
          } else {
            // Use actual execution time if available, otherwise estimate
            duration = (execution.execution_time || 1000) * 1000 / executedNodes.length;
          }

          // If node starts with 'dummy', it's likely a continuation artifact
          if (actualNodeId && actualNodeId.startsWith('dummy')) {
            status = 'completed';
          }

          return {
            nodeId: actualNodeId,
            nodeType,
            status,
            message: `Executed ${nodeType} node: ${actualNodeId}`,
            timestamp: Date.now() - (executedNodes.length - index) * 1000,
            duration,
          };
        });
      } else {
        // Handle detailed node objects
        executionSteps = executedNodes.map((node, index) => ({
          nodeId: node.node_id,
          nodeType: node.node_type || 'unknown',
          status: node.status || 'completed',
          message: `Executed ${node.node_type || 'unknown'} node: ${node.node_id}`,
          timestamp: Date.now() - (executedNodes.length - index) * 1000,
          duration: 1000,
        }));
      }
    } else {
      // Fall back to parsing execution_log
      executionSteps = (execution.execution_log || []).map((logEntry, index) => {
        const isStart = logEntry.includes('Starting execution of');
        const isSuccess = logEntry.includes('Successfully executed');
        const isError = logEntry.includes('Error executing');
        const isFlowComplete = logEntry.includes('Flow completed');
        const isFlowPaused = logEntry.includes('Flow paused for form input');

        let status = 'completed';
        if (isStart) status = 'running';
        if (isError) status = 'failed';
        if (isFlowComplete) status = 'completed';
        if (isFlowPaused) status = 'paused';

        // Extract node ID and type from log entry
        let nodeId = `step_${index}`;
        let nodeType = 'unknown';

        // Pattern 1: "Starting execution of {type} node: {id}"
        const startMatch = logEntry.match(/Starting execution of (\w+(?:-\w+)?) node: ([^\s]+)/);
        if (startMatch) {
          nodeType = startMatch[1];
          nodeId = startMatch[2];
        }

        // Pattern 2: "Successfully executed {type} node: {id}"
        const successMatch = logEntry.match(/Successfully executed (\w+(?:-\w+)?) node: ([^\s]+)/);
        if (successMatch) {
          nodeType = successMatch[1];
          nodeId = successMatch[2]; // Fixed: was using startMatch[2]
        }

        // Pattern 3: "Error executing {type} node: {id}"
        const errorMatch = logEntry.match(/Error executing (\w+(?:-\w+)?) node: ([^\s]+)/);
        if (errorMatch) {
          nodeType = errorMatch[1];
          nodeId = errorMatch[2];
        }

        // Pattern 4: "Flow paused for form input at {type} node: {id}"
        const pauseMatch = logEntry.match(/Flow paused for form input at (\w+(?:-\w+)?) node: ([^\s]+)/);
        if (pauseMatch) {
          nodeType = pauseMatch[1];
          nodeId = pauseMatch[2];
        }

        return {
          nodeId,
          nodeType,
          status,
          message: logEntry,
          timestamp: Date.now() - ((execution.execution_log || []).length - index) * 1000, // Approximate timestamps
          duration: 1000, // Approximate duration
        };
      });
    }

    // Calculate steps from execution details or fall back to execution log
    const totalSteps = executedNodes.length > 0 ? executedNodes.length : (execution.execution_log || []).length || 0;
    const completedSteps = executionSteps.filter((s) => s.status === 'completed').length || 0;
    const failedSteps = executionSteps.filter((s) => s.status === 'failed').length || 0;
    const pendingSteps = executionSteps.filter((s) => s.status === 'pending').length || 0;
    const runningSteps = executionSteps.filter((s) => s.status === 'running').length || 0;
    const skippedSteps = executionSteps.filter((s) => s.status === 'skipped').length || 0;
    const progress = totalSteps > 0 ? (completedSteps / totalSteps) * 100 : 0;

    // Extract flow information
    const flowName = execution.flow_name || execution.flow_definition?.name || 'Unnamed Flow';
    const flowDescription = execution.flow_definition?.description || 'No description';
    const finalResult = execution.result || {};
    const finalData = finalResult.data || finalResult.result || 'No result data';

    // Extract execution context and error information
    const context = execution.context || {};
    const executionLog = execution.execution_log || [];
    const { error } = execution;
    const flowCompleted = context.flow_completed || finalResult.flow_completed || execution.status === 'completed' || false;
    const endNodeId = context.end_node_id || finalResult.end_node_id || executionDetails.current_node;

    // Check for specific issues
    const hasPendingNodes = pendingSteps > 0;
    const hasFailedNodes = failedSteps > 0;
    const hasRunningNodes = runningSteps > 0;
    const isFlowCompleted = flowCompleted || execution.status === 'completed' || execution.status === 'complete'
                           || finalResult.flow_completed || execution.result?.status === 'completed'
                           || finalResult.success === true;
    const hasErrors = error || hasFailedNodes;

    // Check if flow is paused waiting for form input
    const isPausedForForm = execution.waiting_for_form_input || execution.form_node_waiting
                           || context.waiting_for_form_input || context.form_node_waiting
                           || finalResult.waiting_for_form_input || finalResult.form_node_waiting;

    // Determine overall status
    let overallStatus = 'success';
    let statusColor = 'success';
    let statusMessage = 'Flow completed successfully';

    if (hasFailedNodes) {
      overallStatus = 'failed';
      statusColor = 'error';
      statusMessage = `${failedSteps} node(s) failed`;
    } else if (isPausedForForm) {
      overallStatus = 'paused';
      statusColor = 'warning';
      statusMessage = 'Flow paused waiting for form input';
    } else if (hasPendingNodes && !isFlowCompleted) {
      overallStatus = 'pending';
      statusColor = 'warning';
      statusMessage = `${pendingSteps} node(s) pending`;
    } else if (hasRunningNodes) {
      overallStatus = 'running';
      statusColor = 'primary';
      statusMessage = 'Flow is running';
    } else if (!isFlowCompleted) {
      overallStatus = 'incomplete';
      statusColor = 'warning';
      statusMessage = 'Flow did not complete properly';
    }

    return (
      <Card
        key={execution.execution_id}
        sx={{
          mb: 2,
          ...(execution.status === 'paused' && {
            border: 2,
            borderColor: 'warning.main',
            backgroundColor: 'warning.50',
            '@keyframes pulse': {
              '0%': { boxShadow: '0 0 0 0 rgba(255, 152, 0, 0.4)' },
              '70%': { boxShadow: '0 0 0 10px rgba(255, 152, 0, 0)' },
              '100%': { boxShadow: '0 0 0 0 rgba(255, 152, 0, 0)' },
            },
            animation: 'pulse 2s infinite',
          }),
          ...(execution.status === 'debugging' && {
            border: 2,
            borderColor: 'info.main',
            backgroundColor: 'info.50',
          }),
        }}
      >
        <CardHeader
          title={(
            <Typography variant="h6">
              {flowName}
            </Typography>
          )}
          action={(
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Chip
                  label={overallStatus.toUpperCase()}
                  color={statusColor}
                  size="small"
                />
                {execution.isInitialExecution && (
                  <Chip
                    label="INITIAL"
                    color="info"
                    size="small"
                    variant="outlined"
                  />
                )}
                {hasFailedNodes && (
                  <Chip
                    label={`${failedSteps} Failed`}
                    color="error"
                    size="small"
                    variant="outlined"
                  />
                )}
                {hasPendingNodes && (
                  <Chip
                    label={`${pendingSteps} Pending`}
                    color="warning"
                    size="small"
                    variant="outlined"
                  />
                )}
              </Box>
              <IconButton onClick={() => handleViewDetails(execution)}>
                <VisibilityIcon />
              </IconButton>
              {(execution.status === 'paused' || execution.status === 'debugging') && (
                <IconButton
                  onClick={() => handleOpenDebug(execution)}
                  color="info"
                  title="Debug Execution"
                >
                  <DebugIcon />
                </IconButton>
              )}
            </Box>
          )}
          subheader={(
            <Box sx={{
              display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap',
            }}
            >
              <Typography variant="body2" color="text.secondary">
                ID:
                {' '}
                {execution.execution_id}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Duration:
                {' '}
                {execution.execution_time}
                ms
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Steps:
                {' '}
                {executedNodes.length}
                /
                {totalSteps}
              </Typography>
              {failedSteps > 0 && (
                <Typography variant="body2" color="error">
                  Failed:
                  {' '}
                  {failedSteps}
                </Typography>
              )}
              {pendingSteps > 0 && (
                <Typography variant="body2" color="warning">
                  Pending:
                  {' '}
                  {pendingSteps}
                </Typography>
              )}
              {skippedSteps > 0 && (
                <Typography variant="body2" color="info">
                  Skipped:
                  {' '}
                  {skippedSteps}
                </Typography>
              )}
            </Box>
          )}
        />
        <CardContent>
          <LinearProgress
            variant="determinate"
            value={progress}
            color={hasFailedNodes ? 'error' : hasPendingNodes ? 'warning' : 'primary'}
            sx={{ mb: 2 }}
          />

          {/* Status Summary */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Status:
              {' '}
              {statusMessage}
            </Typography>
            {endNodeId && (
              <Typography variant="body2" color="text.secondary">
                End Node:
                {' '}
                {endNodeId}
              </Typography>
            )}
            {flowCompleted && (
              <Typography variant="body2" color="success.main">
                ✓ Flow marked as completed
              </Typography>
            )}
          </Box>

          {/* Error Information */}
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              <Typography variant="body2" fontWeight="bold">Flow Error:</Typography>
              <Typography variant="body2">{error}</Typography>
            </Alert>
          )}

          {/* Execution Log */}
          {executionLog.length > 0 && (
            <Accordion sx={{ mb: 2 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="body2">
                  Execution Log (
                  {executionLog.length}
                  {' '}
                  entries)
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Stack spacing={1}>
                  {executionLog.map((logEntry, index) => (
                    <Typography key={index} variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                      {logEntry}
                    </Typography>
                  ))}
                </Stack>
              </AccordionDetails>
            </Accordion>
          )}

          {/* Final Result */}
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="body2">Final Result</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <pre style={{ fontSize: '0.8rem', overflow: 'auto', maxHeight: '200px' }}>
                {JSON.stringify(finalData, null, 2)}
              </pre>
            </AccordionDetails>
          </Accordion>
        </CardContent>
      </Card>
    );
  };

  // Execution replay handler functions
  const handleOpenReplay = (execution) => {
    setCurrentReplayExecution(execution);
    setReplayDialogOpen(true);
    setCurrentReplayStep(0);
    setReplayElapsedTime(0);
    setIsReplayPlaying(false);
  };

  const handleCloseReplay = () => {
    setReplayDialogOpen(false);
    setIsReplayPlaying(false);
    setCurrentReplayStep(0);
    setReplayElapsedTime(0);
    if (replayTimerRef.current) {
      clearInterval(replayTimerRef.current);
      replayTimerRef.current = null;
    }
  };

  const handleReplayPlayPause = () => {
    if (isReplayPlaying) {
      setIsReplayPlaying(false);
      if (replayTimerRef.current) {
        clearInterval(replayTimerRef.current);
        replayTimerRef.current = null;
      }
    } else {
      setIsReplayPlaying(true);
      startReplayTimer();
    }
  };

  const handleReplayStop = () => {
    setIsReplayPlaying(false);
    setCurrentReplayStep(0);
    setReplayElapsedTime(0);
    if (replayTimerRef.current) {
      clearInterval(replayTimerRef.current);
      replayTimerRef.current = null;
    }
  };

  const handleReplayReset = () => {
    setCurrentReplayStep(0);
    setReplayElapsedTime(0);
    setIsReplayPlaying(false);
    if (replayTimerRef.current) {
      clearInterval(replayTimerRef.current);
      replayTimerRef.current = null;
    }
  };

  const startReplayTimer = () => {
    const stepDuration = 2000 / replaySpeed; // 2 seconds per step at 1x speed
    replayTimerRef.current = setInterval(() => {
      setCurrentReplayStep((prev) => {
        const nextStep = prev + 1;
        if (nextStep >= (currentReplayExecution?.execution_steps?.length || 0)) {
          setIsReplayPlaying(false);
          if (replayTimerRef.current) {
            clearInterval(replayTimerRef.current);
            replayTimerRef.current = null;
          }
          return prev;
        }
        return nextStep;
      });
      setReplayElapsedTime((prev) => prev + stepDuration);
    }, stepDuration);
  };

  // Variable inspector handler functions
  const handleOpenVariableInspector = (execution) => {
    setCurrentInspectedExecution(execution);
    setVariableInspectorOpen(true);
    setSelectedVariable(null);
    setVariableSearchTerm('');
    setSelectedVariableCategory(null);

    // Extract variables from execution data
    const variables = extractVariablesFromExecution(execution);
    setFilteredVariables(variables);
  };

  const handleCloseVariableInspector = () => {
    setVariableInspectorOpen(false);
    setCurrentInspectedExecution(null);
    setSelectedVariable(null);
    setVariableSearchTerm('');
    setSelectedVariableCategory(null);
    setFilteredVariables([]);
  };

  const extractVariablesFromExecution = (execution) => {
    const variables = [];

    // Extract from context variables
    if (execution.context_variables) {
      Object.entries(execution.context_variables).forEach(([key, value]) => {
        variables.push({
          name: key,
          value,
          type: typeof value,
          scope: 'context',
          lastModified: execution.updated_at,
          mutable: true,
          history: [],
        });
      });
    }

    // Extract from state variables
    if (execution.state_variables) {
      Object.entries(execution.state_variables).forEach(([key, value]) => {
        variables.push({
          name: key,
          value,
          type: typeof value,
          scope: 'state',
          lastModified: execution.updated_at,
          mutable: true,
          history: [],
        });
      });
    }

    // Extract from execution steps
    if (execution.execution_steps) {
      execution.execution_steps.forEach((step, index) => {
        if (step.output_data) {
          Object.entries(step.output_data).forEach(([key, value]) => {
            variables.push({
              name: `${step.node_id}.${key}`,
              value,
              type: typeof value,
              scope: 'local',
              lastModified: step.timestamp || execution.updated_at,
              mutable: false,
              history: [],
            });
          });
        }
      });
    }

    return variables;
  };

  // Format duration helper
  const formatDuration = (ms) => {
    if (!ms) return '0ms';
    if (ms < 1000) return `${ms}ms`;
    if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
    return `${(ms / 60000).toFixed(1)}m`;
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Enhanced Header with streaming controls */}
      <Box sx={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3,
      }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="h4">
            Flow Execution Tracker
          </Typography>
          {streamingMode && (
            <Chip
              label="Streaming Active"
              color="success"
              size="small"
              icon={(
                <Box sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  bgcolor: 'success.main',
                  animation: 'pulse 2s infinite',
                }}
                />
              )}
            />
          )}
          {streamingConnections.size > 0 && (
            <Chip
              label={`${streamingConnections.size} Live`}
              color="info"
              size="small"
            />
          )}
          <Tooltip title="Keyboard Shortcuts: Ctrl+R (Refresh), Ctrl+F (Search), Ctrl+T (Toggle Auto-Retry), Esc (Close Modals)">
            <IconButton size="small" color="info">
              <InfoIcon />
            </IconButton>
          </Tooltip>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="contained"
            startIcon={<RefreshIcon />}
            onClick={fetchExecutions}
            disabled={loading}
          >
            Refresh
          </Button>
          {systemHealthData && (
            <Tooltip title={`CPU: ${systemHealthData.cpu_usage}% | Memory: ${systemHealthData.memory_usage}%`}>
              <Button
                variant="outlined"
                startIcon={<MonitorIcon />}
                onClick={loadSystemHealth}
                color={systemHealthData.cpu_usage > 80 ? 'warning' : 'primary'}
              >
                Health
              </Button>
            </Tooltip>
          )}
          <Button
            variant="outlined"
            startIcon={<CleaningServicesIcon />}
            onClick={() => handleCleanupExecutions(10)}
            disabled={cleanupLoading}
          >
            Keep Recent 10 Executions
          </Button>
        </Box>
      </Box>

      {/* Quick stats bar */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={3}>
            <Grid item xs={6} md={3}>
              <Typography variant="h4" color="primary">
                {executions.length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total Executions
              </Typography>
            </Grid>
            <Grid item xs={6} md={3}>
              <Typography variant="h4" color="success.main">
                {executions.filter((e) => e.status === 'completed' || e.status === 'success').length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Successful
              </Typography>
            </Grid>
            <Grid item xs={6} md={3}>
              <Typography variant="h4" color="error.main">
                {executions.filter((e) => e.status === 'failed' || e.error).length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Failed
              </Typography>
            </Grid>
            <Grid item xs={6} md={3}>
              <Typography variant="h4" color="warning.main">
                {executions.filter((e) => e.status === 'running' || e.status === 'pending').length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Running/Pending
              </Typography>
            </Grid>
            {/* NEW: Streaming Stats */}
            {(streamingMode || streamingConnections.size > 0) && (
              <>
                <Grid item xs={6} md={3}>
                  <Typography variant="h4" color="info.main">
                    {streamingConnections.size}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Live Streams
                  </Typography>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Typography variant="h4" color="secondary.main">
                    {recoveryOptions.size}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Recoverable
                  </Typography>
                </Grid>
              </>
            )}
          </Grid>
        </CardContent>
      </Card>

      {/* Enhanced Performance Dashboard */}
      <Card sx={{ mb: 3 }}>
        <CardHeader
          title="Performance Dashboard"
          action={(
            <Box sx={{ display: 'flex', gap: 1 }}>
              <FormControlLabel
                control={(
                  <Switch
                    checked={errorHandlingState.autoRetryEnabled}
                    onChange={(e) => setErrorHandlingState((prev) => ({
                      ...prev,
                      autoRetryEnabled: e.target.checked,
                    }))}
                    color="primary"
                  />
                )}
                label="Auto-Retry"
              />
              {errorHandlingState.retryQueue.length > 0 && (
                <Chip
                  label={`${errorHandlingState.retryQueue.length} in queue`}
                  color="warning"
                  size="small"
                />
              )}
            </Box>
          )}
        />
        <CardContent>
          <Grid container spacing={3}>
            {/* Error Categories */}
            <Grid item xs={12} md={6}>
              <Typography variant="h6" gutterBottom>Error Analysis</Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {['timeout', 'network', 'validation', 'permission', 'rate_limit', 'server_error'].map((category) => {
                  const count = executions.filter((e) => e.error && categorizeError(e.error) === category).length;
                  return count > 0 ? (
                    <Chip
                      key={category}
                      label={`${category}: ${count}`}
                      color={category === 'timeout' || category === 'network' ? 'warning' : 'error'}
                      size="small"
                    />
                  ) : null;
                })}
              </Box>
            </Grid>

            {/* Performance Metrics */}
            <Grid item xs={12} md={6}>
              <Typography variant="h6" gutterBottom>Performance Metrics</Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2">Avg Execution Time:</Typography>
                  <Typography variant="body2" fontWeight="bold">
                    {executions.length > 0
                      ? formatDuration(
                        executions.reduce((sum, e) => sum + (e.execution_time || 0), 0) / executions.length,
                      )
                      : 'N/A'}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2">Success Rate:</Typography>
                  <Typography variant="body2" fontWeight="bold">
                    {executions.length > 0
                      ? `${((executions.filter((e) => e.status === 'completed' || e.status === 'success').length / executions.length) * 100).toFixed(1)}%`
                      : 'N/A'}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2">Auto-Retry Enabled:</Typography>
                  <Typography variant="body2" fontWeight="bold" color={errorHandlingState.autoRetryEnabled ? 'success.main' : 'text.secondary'}>
                    {errorHandlingState.autoRetryEnabled ? 'Yes' : 'No'}
                  </Typography>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Enhanced tabs interface */}
      <Paper sx={{ mb: 3 }}>
        <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)}>
          <Tab label="Executions" icon={<TimelineIcon />} />
          <Tab
            label="Live Monitor"
            icon={(
              <Badge
                badgeContent={activeExecutions.size}
                color="primary"
                invisible={activeExecutions.size === 0}
              >
                <MonitorIcon />
              </Badge>
            )}
          />
          {debugMode && <Tab label="Debug Monitor" icon={<DebugIcon />} />}
          {performanceMode && <Tab label="Performance" icon={<PerformanceIcon />} />}
          {conditionalFlowMode && <Tab label="Conditional Flow" icon={<ConditionalIcon />} />}
          <Tab label="Settings" icon={<ConfigIcon />} />
        </Tabs>
      </Paper>

      {/* Tab content */}
      {activeTab === 0 && (
        <Box>
          {/* Search and filter bar */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="Search executions..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    InputProps={{
                      startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                    }}
                  />
                </Grid>
                <Grid item xs={6} md={2}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Status</InputLabel>
                    <Select
                      value={filterOptions.status}
                      onChange={(e) => setFilterOptions((prev) => ({ ...prev, status: e.target.value }))}
                      label="Status"
                    >
                      <MenuItem value="all">All Status</MenuItem>
                      <MenuItem value="pending">Pending</MenuItem>
                      <MenuItem value="running">Running</MenuItem>
                      <MenuItem value="completed">Completed</MenuItem>
                      <MenuItem value="failed">Failed</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={6} md={2}>
                  <TextField
                    fullWidth
                    size="small"
                    type="date"
                    label="From Date"
                    value={filterOptions.dateFrom || ''}
                    onChange={(e) => setFilterOptions((prev) => ({ ...prev, dateFrom: e.target.value }))}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={6} md={2}>
                  <TextField
                    fullWidth
                    size="small"
                    type="date"
                    label="To Date"
                    value={filterOptions.dateTo || ''}
                    onChange={(e) => setFilterOptions((prev) => ({ ...prev, dateTo: e.target.value }))}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={6} md={2}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Duration</InputLabel>
                    <Select
                      value={filterOptions.duration || 'all'}
                      onChange={(e) => setFilterOptions((prev) => ({ ...prev, duration: e.target.value }))}
                      label="Duration"
                    >
                      <MenuItem value="all">All Durations</MenuItem>
                      <MenuItem value="fast">&lt; 1 second</MenuItem>
                      <MenuItem value="medium">1-10 seconds</MenuItem>
                      <MenuItem value="slow">&gt; 10 seconds</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={6} md={2}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Sort By</InputLabel>
                    <Select
                      value={sortBy}
                      onChange={(e) => setSortBy(e.target.value)}
                      label="Sort By"
                    >
                      <MenuItem value="timestamp">Timestamp</MenuItem>
                      <MenuItem value="duration">Duration</MenuItem>
                      <MenuItem value="status">Status</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Stack direction="row" spacing={1} justifyContent="flex-end">
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => {
                        setFilterOptions({
                          status: 'all',
                          nodeType: 'all',
                          dateRange: 'all',
                          duration: 'all',
                        });
                        setSearchQuery('');
                      }}
                    >
                      Clear Filters
                    </Button>
                    <FormControlLabel
                      control={(
                        <Switch
                          checked={autoRefresh}
                          onChange={(e) => setAutoRefresh(e.target.checked)}
                          size="small"
                        />
                      )}
                      label="Auto-refresh"
                    />
                  </Stack>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Executions list */}
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : authError ? (
            <Alert severity="error">
              Authentication required. Please log in to view executions.
            </Alert>
          ) : filteredExecutions.length === 0 ? (
            <Alert severity="info">
              No executions found.
              {' '}
              {searchQuery && `No results for "${searchQuery}"`}
            </Alert>
          ) : (
            <Stack spacing={2}>
              {filteredExecutions.map((execution) => renderExecutionCard(execution))}
            </Stack>
          )}
        </Box>
      )}

      {activeTab === 1 && (
        <Box>
          {/* Enhanced real-time execution monitor */}
          <Box sx={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3,
          }}
          >
            <Typography variant="h6">
              Live Execution Monitor
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Chip
                icon={websocketConnected ? <SuccessIcon /> : <ErrorIcon />}
                label={websocketConnected ? 'WebSocket' : 'Disconnected'}
                color={websocketConnected ? 'success' : 'error'}
                variant="outlined"
              />
              {streamingMode && (
                <Chip
                  icon={(
                    <Box sx={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      bgcolor: 'success.main',
                      animation: 'pulse 2s infinite',
                    }}
                    />
                  )}
                  label="SSE Streaming"
                  color="success"
                  variant="outlined"
                />
              )}
              <FormControlLabel
                control={(
                  <Switch
                    checked={realTimeMode}
                    onChange={toggleRealTimeMode}
                    color="primary"
                  />
                )}
                label="Real-time Mode"
              />
              <FormControlLabel
                control={(
                  <Switch
                    checked={streamingMode}
                    onChange={(e) => setStreamingMode(e.target.checked)}
                    color="success"
                  />
                )}
                label="Streaming Mode"
              />
            </Box>
          </Box>

          {!realTimeMode ? (
            <Alert severity="info" sx={{ mb: 3 }}>
              <AlertTitle>Real-time Mode Disabled</AlertTitle>
              Enable real-time mode to see live execution updates, node status changes, and performance metrics.
            </Alert>
          ) : !websocketConnected ? (
            <Alert severity="warning" sx={{ mb: 3 }}>
              <AlertTitle>WebSocket Disconnected</AlertTitle>
              Unable to connect to real-time updates. Check your connection and try again.
            </Alert>
          ) : (
            <Grid container spacing={3}>
              {/* Active Executions */}
              <Grid item xs={12} md={8}>
                <Card>
                  <CardHeader
                    title={`Active Executions (${activeExecutions.size})`}
                    action={(
                      <IconButton onClick={fetchExecutions} size="small">
                        <RefreshIcon />
                      </IconButton>
                    )}
                  />
                  <CardContent>
                    {activeExecutions.size === 0 ? (
                      <Box sx={{ textAlign: 'center', py: 4 }}>
                        <Typography variant="body2" color="text.secondary">
                          No active executions
                        </Typography>
                      </Box>
                    ) : (
                      <Stack spacing={2}>
                        {Array.from(activeExecutions.values()).map((execution) => (
                          <Fade in key={execution.execution_id}>
                            <Card
                              variant="outlined"
                              sx={{
                                border: 2,
                                borderColor: execution.status === 'running' ? 'primary.main'
                                  : execution.status === 'completed' ? 'success.main' : 'error.main',
                              }}
                            >
                              <CardContent>
                                <Box sx={{
                                  display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2,
                                }}
                                >
                                  <Typography variant="h6">
                                    {execution.flow_name || 'Unnamed Flow'}
                                  </Typography>
                                  <Chip
                                    label={execution.status.toUpperCase()}
                                    color={execution.status === 'running' ? 'primary'
                                      : execution.status === 'completed' ? 'success' : 'error'}
                                    size="small"
                                  />
                                </Box>

                                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                  ID:
                                  {' '}
                                  {execution.execution_id}
                                </Typography>

                                {/* Execution Progress */}
                                <Box sx={{ mb: 2 }}>
                                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                    <Typography variant="caption">
                                      Progress:
                                      {' '}
                                      {execution.nodes.size}
                                      {' '}
                                      nodes executed
                                    </Typography>
                                    <Typography variant="caption">
                                      {execution.duration ? `${(execution.duration / 1000).toFixed(1)}s` : 'Running...'}
                                    </Typography>
                                  </Box>
                                  <LinearProgress
                                    variant="determinate"
                                    value={execution.nodes.size * 10}
                                    sx={{ height: 8, borderRadius: 4 }}
                                  />
                                </Box>

                                {/* Current State Variables */}
                                {execution.execution_state && Object.keys(execution.execution_state).length > 0 && (
                                  <Box sx={{ mb: 2 }}>
                                    <Typography variant="subtitle2" gutterBottom>
                                      Current State Variables:
                                    </Typography>
                                    <Box sx={{
                                      p: 1,
                                      bgcolor: 'grey.50',
                                      borderRadius: 1,
                                      border: 1,
                                      borderColor: 'divider',
                                      maxHeight: 150,
                                      overflow: 'auto',
                                    }}
                                    >
                                      <Stack spacing={0.5}>
                                        {Object.entries(execution.execution_state).map(([key, value]) => (
                                          <Box key={key} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <Typography variant="caption" sx={{ fontWeight: 500, minWidth: '30%' }}>
                                              {key}
                                              :
                                            </Typography>
                                            <Typography
                                              variant="caption"
                                              sx={{
                                                fontFamily: 'monospace',
                                                bgcolor: 'primary.50',
                                                px: 0.5,
                                                borderRadius: 0.5,
                                                maxWidth: '65%',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap',
                                              }}
                                            >
                                              {typeof value === 'object' ? `${JSON.stringify(value).substring(0, 30)}...` : String(value)}
                                            </Typography>
                                          </Box>
                                        ))}
                                      </Stack>
                                    </Box>
                                  </Box>
                                )}
                              </CardContent>
                            </Card>
                          </Fade>
                        ))}
                      </Stack>
                    )}
                  </CardContent>
                </Card>
              </Grid>

              {/* Live Updates & Performance */}
              <Grid item xs={12} md={4}>
                <Stack spacing={3}>
                  {/* Live Node Updates */}
                  <Card>
                    <CardHeader title="Live Node Updates" />
                    <CardContent>
                      <Box sx={{ maxHeight: 300, overflow: 'auto' }}>
                        {liveNodeUpdates.size === 0 ? (
                          <Typography variant="body2" color="text.secondary">
                            No recent updates
                          </Typography>
                        ) : (
                          <Stack spacing={1}>
                            {Array.from(liveNodeUpdates.values())
                              .sort((a, b) => b.timestamp - a.timestamp)
                              .slice(0, 10)
                              .map((update, index) => (
                                <Slide direction="left" in key={`${update.execution_id}_${update.node_id}_${index}`}>
                                  <Box sx={{
                                    p: 1,
                                    border: 1,
                                    borderColor: 'divider',
                                    borderRadius: 1,
                                    bgcolor: update.type === 'completed' ? 'success.50'
                                      : update.type === 'failed' ? 'error.50' : 'primary.50',
                                  }}
                                  >
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                      <Avatar sx={{ width: 20, height: 20, fontSize: '0.6rem' }}>
                                        {getNodeIcon(update.node_type)}
                                      </Avatar>
                                      <Typography variant="caption" sx={{ flexGrow: 1 }}>
                                        {update.node_id}
                                      </Typography>
                                      <Chip
                                        label={update.type}
                                        size="small"
                                        color={update.type === 'completed' ? 'success'
                                          : update.type === 'failed' ? 'error' : 'primary'}
                                        variant="outlined"
                                      />
                                    </Box>
                                    <Typography variant="caption" color="text.secondary">
                                      {new Date(update.timestamp).toLocaleTimeString()}
                                    </Typography>
                                  </Box>
                                </Slide>
                              ))}
                          </Stack>
                        )}
                      </Box>
                    </CardContent>
                  </Card>

                  {/* Performance Metrics */}
                  <Card>
                    <CardHeader title="Performance Metrics" />
                    <CardContent>
                      <Stack spacing={2}>
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            Active Executions
                          </Typography>
                          <Typography variant="h6">
                            {activeExecutions.size}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            Total Node Updates
                          </Typography>
                          <Typography variant="h6">
                            {liveNodeUpdates.size}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            Live Logs
                          </Typography>
                          <Typography variant="h6">
                            {liveExecutionLogs.length}
                          </Typography>
                        </Box>
                      </Stack>
                    </CardContent>
                  </Card>

                  {/* Live State Variables */}
                  <Card>
                    <CardHeader title="Live State Variables" />
                    <CardContent>
                      <Box sx={{ maxHeight: 300, overflow: 'auto' }}>
                        {activeExecutions.size === 0 ? (
                          <Typography variant="body2" color="text.secondary">
                            No active executions to show state variables
                          </Typography>
                        ) : (
                          <Stack spacing={2}>
                            {Array.from(activeExecutions.values()).map((execution) => (
                              <Box key={execution.execution_id}>
                                <Typography variant="subtitle2" gutterBottom>
                                  {execution.flow_name || 'Unnamed Flow'}
                                </Typography>
                                <Box sx={{
                                  p: 1,
                                  bgcolor: 'grey.50',
                                  borderRadius: 1,
                                  border: 1,
                                  borderColor: 'divider',
                                }}
                                >
                                  {Object.keys(execution.execution_state || {}).length === 0 ? (
                                    <Typography variant="caption" color="text.secondary">
                                      No state variables yet
                                    </Typography>
                                  ) : (
                                    <Stack spacing={0.5}>
                                      {Object.entries(execution.execution_state || {}).map(([key, value]) => (
                                        <Box key={key} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                          <Typography variant="caption" sx={{ fontWeight: 500 }}>
                                            {key}
                                            :
                                          </Typography>
                                          <Typography
                                            variant="caption"
                                            sx={{
                                              fontFamily: 'monospace',
                                              bgcolor: 'primary.50',
                                              px: 0.5,
                                              borderRadius: 0.5,
                                              maxWidth: '60%',
                                              overflow: 'hidden',
                                              textOverflow: 'ellipsis',
                                            }}
                                          >
                                            {typeof value === 'object' ? `${JSON.stringify(value).substring(0, 50)}...` : String(value)}
                                          </Typography>
                                        </Box>
                                      ))}
                                    </Stack>
                                  )}
                                </Box>
                              </Box>
                            ))}
                          </Stack>
                        )}
                      </Box>
                    </CardContent>
                  </Card>
                </Stack>
              </Grid>
            </Grid>
          )}

          {/* NEW: Recovery Section */}
          {recoveryOptions.size > 0 && (
            <Card sx={{ mt: 3 }}>
              <CardHeader
                title="Flow Recovery Options"
                subheader={`${recoveryOptions.size} executions can be recovered`}
                action={(
                  <Chip
                    label="Recovery Available"
                    color="warning"
                    size="small"
                    icon={<RestartIcon />}
                  />
                )}
              />
              <CardContent>
                <Stack spacing={2}>
                  {Array.from(recoveryOptions.entries()).map(([executionId, recovery]) => (
                    <Alert
                      key={executionId}
                      severity="warning"
                      action={(
                        <Button
                          color="inherit"
                          size="small"
                          onClick={() => handleStreamingRecovery(executionId)}
                          startIcon={<RestartIcon />}
                        >
                          Recover
                        </Button>
                      )}
                    >
                      <Typography variant="body2" fontWeight="bold">
                        Execution
                        {' '}
                        {executionId.slice(-8)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Error:
                        {' '}
                        {recovery.errorMessage}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        Failed:
                        {' '}
                        {new Date(recovery.timestamp).toLocaleString()}
                      </Typography>
                    </Alert>
                  ))}
                </Stack>
              </CardContent>
            </Card>
          )}
        </Box>
      )}

      {activeTab === 2 && debugMode && (
        <Box>
          <Typography variant="h6" gutterBottom>Debug Monitor</Typography>

          {/* System Status */}
          {systemStatus && (
            <Card sx={{ mb: 2 }}>
              <CardHeader title="System Status" />
              <CardContent>
                <Grid container spacing={2}>
                  <Grid item xs={6} md={3}>
                    <Typography variant="caption" color="text.secondary">Active Executions</Typography>
                    <Typography variant="h6">{systemStatus.active_executions || 0}</Typography>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Typography variant="caption" color="text.secondary">Memory Usage</Typography>
                    <Typography variant="h6">{formatMemoryUsage(systemStatus.memory_usage)}</Typography>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Typography variant="caption" color="text.secondary">CPU Usage</Typography>
                    <Typography variant="h6">{systemStatus.cpu_usage ? `${systemStatus.cpu_usage.toFixed(1)}%` : 'N/A'}</Typography>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Typography variant="caption" color="text.secondary">Uptime</Typography>
                    <Typography variant="h6">{formatDuration(systemStatus.uptime)}</Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          )}

          {/* Error Log */}
          {errorLog.length > 0 && (
            <Card sx={{ mb: 2 }}>
              <CardHeader title={`Error Log (${errorLog.length})`} />
              <CardContent>
                <Stack spacing={1}>
                  {errorLog.slice(0, 10).map((error, index) => (
                    <Alert key={index} severity="error" sx={{ fontSize: '0.8rem' }}>
                      <Typography variant="body2" fontWeight="bold">{error.timestamp}</Typography>
                      <Typography variant="body2">{error.message}</Typography>
                    </Alert>
                  ))}
                </Stack>
              </CardContent>
            </Card>
          )}

          {/* Execution Traces */}
          {executionTraces.length > 0 && (
            <Card>
              <CardHeader title={`Execution Traces (${executionTraces.length})`} />
              <CardContent>
                <Stack spacing={1}>
                  {executionTraces.slice(0, 5).map((trace, index) => (
                    <Paper key={index} variant="outlined" sx={{ p: 1 }}>
                      <Typography variant="body2" fontWeight="bold">{trace.execution_id}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Duration:
                        {' '}
                        {formatDuration(trace.duration)}
                        {' '}
                        | Nodes:
                        {' '}
                        {trace.nodes_executed}
                      </Typography>
                    </Paper>
                  ))}
                </Stack>
              </CardContent>
            </Card>
          )}
        </Box>
      )}

      {activeTab === 2 && performanceMode && (
        <Box>
          <Typography variant="h6" gutterBottom>Performance Metrics</Typography>

          {performanceMetrics ? (
            <Grid container spacing={2}>
              {performanceMetrics.map((metric, index) => (
                <Grid item xs={12} md={6} key={index}>
                  <Card>
                    <CardHeader title={`Performance Metric ${index + 1}`} />
                    <CardContent>
                      <Grid container spacing={2}>
                        {metric.cpu && (
                          <Grid item xs={6}>
                            <Typography variant="caption" color="text.secondary">CPU</Typography>
                            <Typography variant="h6">
                              {metric.cpu}
                              %
                            </Typography>
                          </Grid>
                        )}
                        {metric.memory && (
                          <Grid item xs={6}>
                            <Typography variant="caption" color="text.secondary">Memory</Typography>
                            <Typography variant="h6">{formatMemoryUsage(metric.memory)}</Typography>
                          </Grid>
                        )}
                        {metric.execution_time && (
                          <Grid item xs={6}>
                            <Typography variant="caption" color="text.secondary">Execution Time</Typography>
                            <Typography variant="h6">{formatDuration(metric.execution_time)}</Typography>
                          </Grid>
                        )}
                        {metric.throughput && (
                          <Grid item xs={6}>
                            <Typography variant="caption" color="text.secondary">Throughput</Typography>
                            <Typography variant="h6">
                              {metric.throughput}
                              {' '}
                              ops/s
                            </Typography>
                          </Grid>
                        )}
                      </Grid>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          ) : (
            <Alert severity="info">No performance metrics available.</Alert>
          )}
        </Box>
      )}

      {activeTab === 3 && conditionalFlowMode && (
        <Box>
          <Typography variant="h6" gutterBottom>Conditional Flow Analysis</Typography>

          {conditionalRoutes.length > 0 ? (
            <Stack spacing={2}>
              {conditionalRoutes.map((route, index) => (
                <Card key={index}>
                  <CardHeader title={`Conditional Routes - ${route.execution_id}`} />
                  <CardContent>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <Typography variant="subtitle2" gutterBottom>Active Routes</Typography>
                        <Stack spacing={1}>
                          {route.routes.active_routes.map((activeRoute, idx) => (
                            <Chip
                              key={idx}
                              label={`${activeRoute.target} (${activeRoute.condition})`}
                              color="success"
                              size="small"
                            />
                          ))}
                        </Stack>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Typography variant="subtitle2" gutterBottom>Inactive Routes</Typography>
                        <Stack spacing={1}>
                          {route.routes.inactive_routes.map((inactiveRoute, idx) => (
                            <Chip
                              key={idx}
                              label={`${inactiveRoute.target} (${inactiveRoute.condition})`}
                              color="default"
                              size="small"
                              variant="outlined"
                            />
                          ))}
                        </Stack>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              ))}
            </Stack>
          ) : (
            <Alert severity="info">No conditional flow data available.</Alert>
          )}
        </Box>
      )}

      {activeTab === 4 && (
        <Box>
          <Typography variant="h6" gutterBottom>Settings & Configuration</Typography>

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card>
                <CardHeader title="Monitoring Options" />
                <CardContent>
                  <Stack spacing={2}>
                    <FormControlLabel
                      control={(
                        <Switch
                          checked={debugMode}
                          onChange={(e) => setDebugMode(e.target.checked)}
                        />
                      )}
                      label="Debug Mode"
                    />
                    <FormControlLabel
                      control={(
                        <Switch
                          checked={performanceMode}
                          onChange={(e) => setPerformanceMode(e.target.checked)}
                        />
                      )}
                      label="Performance Monitoring"
                    />
                    <FormControlLabel
                      control={(
                        <Switch
                          checked={conditionalFlowMode}
                          onChange={(e) => setConditionalFlowMode(e.target.checked)}
                        />
                      )}
                      label="Conditional Flow Analysis"
                    />
                  </Stack>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card>
                <CardHeader title="Advanced Filters" />
                <CardContent>
                  <Stack spacing={2}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Date Range</InputLabel>
                      <Select
                        value={filterOptions.dateRange}
                        onChange={(e) => setFilterOptions((prev) => ({ ...prev, dateRange: e.target.value }))}
                        label="Date Range"
                      >
                        <MenuItem value="all">All Time</MenuItem>
                        <MenuItem value="today">Today</MenuItem>
                        <MenuItem value="week">This Week</MenuItem>
                        <MenuItem value="month">This Month</MenuItem>
                      </Select>
                    </FormControl>

                    <FormControl fullWidth size="small">
                      <InputLabel>Duration</InputLabel>
                      <Select
                        value={filterOptions.duration}
                        onChange={(e) => setFilterOptions((prev) => ({ ...prev, duration: e.target.value }))}
                        label="Duration"
                      >
                        <MenuItem value="all">All Durations</MenuItem>
                        <MenuItem value="fast">Fast (&lt; 5s)</MenuItem>
                        <MenuItem value="medium">Medium (5-30s)</MenuItem>
                        <MenuItem value="slow">Slow (&gt; 30s)</MenuItem>
                      </Select>
                    </FormControl>

                    <FormControl fullWidth size="small">
                      <InputLabel>Node Type</InputLabel>
                      <Select
                        value={filterOptions.nodeType}
                        onChange={(e) => setFilterOptions((prev) => ({ ...prev, nodeType: e.target.value }))}
                        label="Node Type"
                      >
                        <MenuItem value="all">All Types</MenuItem>
                        <MenuItem value="ai-agent">AI Agent</MenuItem>
                        <MenuItem value="rag">RAG</MenuItem>
                        <MenuItem value="form">Form</MenuItem>
                        <MenuItem value="api-call">API Call</MenuItem>
                        <MenuItem value="code-executor">Code Executor</MenuItem>
                        <MenuItem value="conditional">Conditional</MenuItem>
                        <MenuItem value="loop">Loop</MenuItem>
                      </Select>
                    </FormControl>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Box>
      )}

      {/* Enhanced execution details dialog */}
      <Dialog
        open={detailsDialogOpen}
        onClose={handleCloseDetails}
        maxWidth="xl"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <TimelineIcon />
            <Typography variant="h6">
              Execution Details:
              {' '}
              {selectedExecution?.execution_id}
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          {executionDetails && (
            <Box>
              {/* Flow Information */}
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="h5" gutterBottom>
                    {executionDetails.flow_name || executionDetails.flow_definition?.name || 'Unnamed Flow'}
                  </Typography>
                  {executionDetails.flow_definition?.description && (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      {executionDetails.flow_definition.description}
                    </Typography>
                  )}
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <Typography variant="body2" color="text.secondary">
                        Execution ID:
                        {' '}
                        {executionDetails.execution_id}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Status:
                        {' '}
                        <Chip
                          label={executionDetails.status || (executionDetails.success ? 'Success' : 'Failed')}
                          color={executionDetails.status === 'completed' ? 'success'
                            : executionDetails.status === 'running' ? 'primary' : 'error'}
                          size="small"
                        />
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="body2" color="text.secondary">
                        Duration:
                        {' '}
                        {executionDetails.execution_time ? `${executionDetails.execution_time}ms` : 'N/A'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Steps:
                        {' '}
                        {executionDetails.nodes_executed || executionDetails.execution_steps?.length || 0}
                      </Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              {/* Pause Alert for Debugging */}
              {(executionDetails.status === 'paused' || executionDetails.status === 'debugging') && (
                <Alert severity="warning" sx={{ mb: 3 }}>
                  <AlertTitle>Execution Paused for Debugging</AlertTitle>
                  <Typography variant="body2">
                    This execution is currently paused. You can use the debug interface to inspect the current state,
                    modify variables, and resume execution.
                  </Typography>
                  <Box sx={{ mt: 2 }}>
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={<DebugIcon />}
                      onClick={() => {
                        handleCloseDetails();
                        handleOpenDebug(selectedExecution);
                      }}
                      sx={{ mr: 1 }}
                    >
                      Open Debug Interface
                    </Button>
                  </Box>
                </Alert>
              )}

              {/* Enhanced Metrics Summary */}
              {executionDetails.metrics && (
                <Card sx={{ mb: 3 }}>
                  <CardHeader title="Execution Metrics" />
                  <CardContent>
                    <Grid container spacing={2}>
                      <Grid item xs={6} md={3}>
                        <Typography variant="caption" color="text.secondary">Total Nodes</Typography>
                        <Typography variant="h6">{executionDetails.metrics.total_nodes || 0}</Typography>
                      </Grid>
                      <Grid item xs={6} md={3}>
                        <Typography variant="caption" color="text.secondary">Completed Nodes</Typography>
                        <Typography variant="h6" color="success.main">{executionDetails.metrics.completed_nodes || 0}</Typography>
                      </Grid>
                      <Grid item xs={6} md={3}>
                        <Typography variant="caption" color="text.secondary">Failed Nodes</Typography>
                        <Typography variant="h6" color="error.main">{executionDetails.metrics.failed_nodes || 0}</Typography>
                      </Grid>
                      <Grid item xs={6} md={3}>
                        <Typography variant="caption" color="text.secondary">Success Rate</Typography>
                        <Typography variant="h6">
                          {executionDetails.metrics.success_rate ? `${(executionDetails.metrics.success_rate * 100).toFixed(1)}%` : 'N/A'}
                        </Typography>
                      </Grid>
                      {executionDetails.metrics.error_count > 0 && (
                        <Grid item xs={6} md={3}>
                          <Typography variant="caption" color="text.secondary">Error Count</Typography>
                          <Typography variant="h6" color="error.main">{executionDetails.metrics.error_count}</Typography>
                        </Grid>
                      )}
                      {executionDetails.metrics.retry_count > 0 && (
                        <Grid item xs={6} md={3}>
                          <Typography variant="caption" color="text.secondary">Retry Count</Typography>
                          <Typography variant="h6" color="warning.main">{executionDetails.metrics.retry_count}</Typography>
                        </Grid>
                      )}
                    </Grid>
                  </CardContent>
                </Card>
              )}

              {/* Execution Steps */}
              <Typography variant="h6" gutterBottom>
                Execution Steps (
                {executionDetails.execution_steps?.length || 0}
                )
              </Typography>
              <Stepper orientation="vertical" sx={{ mb: 3 }}>
                {(() => {
                  // Use the execution_steps directly from the backend response
                  const executionSteps = executionDetails.execution_steps || [];

                  if (executionSteps.length === 0) {
                    return (
                      <Box sx={{ p: 2, textAlign: 'center', color: 'text.secondary' }}>
                        <Typography variant="body2">No execution steps available</Typography>
                      </Box>
                    );
                  }

                  return executionSteps.map((step, index) => {
                    const nodeId = step.node_id || step.step_id || `step_${index}`;
                    const nodeType = step.node_type || 'unknown';
                    const status = step.status || 'completed';
                    const startTime = step.start_time ? new Date(step.start_time * 1000) : null;
                    const endTime = step.end_time ? new Date(step.end_time * 1000) : null;
                    const duration = step.duration || 0;
                    const inputData = step.input_data || {};
                    const outputData = step.output_data || {};
                    const { error } = step;

                    return (
                      <Step key={index} completed={status === 'completed'}>
                        <StepLabel
                          icon={(
                            <Box sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                              p: 1,
                              borderRadius: 1,
                              bgcolor: status === 'completed' ? 'success.light'
                                : status === 'failed' ? 'error.light' : 'primary.light',
                              color: status === 'completed' ? 'success.contrastText'
                                : status === 'failed' ? 'error.contrastText' : 'primary.contrastText',
                            }}
                            >
                              <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                                {index + 1}
                              </Typography>
                              <Chip
                                label={nodeType}
                                size="small"
                                variant="outlined"
                                sx={{
                                  fontSize: '0.7rem',
                                  height: '20px',
                                  bgcolor: 'white',
                                  color: 'text.primary',
                                }}
                              />
                            </Box>
                          )}
                        >
                          <Box sx={{ mb: 1 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                              {nodeId}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {nodeType}
                              {' '}
                              •
                              {status}
                              {' '}
                              •
                              {duration}
                              ms
                            </Typography>
                            {startTime && (
                              <Typography variant="caption" color="text.secondary">
                                {startTime.toLocaleString()}
                              </Typography>
                            )}
                          </Box>

                          {error && (
                            <Alert severity="error" sx={{ mb: 1 }}>
                              <Typography variant="body2">{error}</Typography>
                            </Alert>
                          )}

                          {(Object.keys(inputData).length > 0 || Object.keys(outputData).length > 0) && (
                            <Accordion>
                              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Typography variant="body2">Step Details</Typography>
                              </AccordionSummary>
                              <AccordionDetails>
                                {Object.keys(inputData).length > 0 && (
                                  <Box sx={{ mb: 2 }}>
                                    <Typography variant="subtitle2" gutterBottom>Input Data:</Typography>
                                    <Box sx={{
                                      p: 1,
                                      bgcolor: 'grey.100',
                                      borderRadius: 1,
                                      fontFamily: 'monospace',
                                      fontSize: '0.8rem',
                                    }}
                                    >
                                      <pre>{JSON.stringify(inputData, null, 2)}</pre>
                                    </Box>
                                  </Box>
                                )}
                                {Object.keys(outputData).length > 0 && (
                                  <Box>
                                    <Typography variant="subtitle2" gutterBottom>Output Data:</Typography>
                                    <Box sx={{
                                      p: 1,
                                      bgcolor: 'grey.100',
                                      borderRadius: 1,
                                      fontFamily: 'monospace',
                                      fontSize: '0.8rem',
                                    }}
                                    >
                                      <pre>{JSON.stringify(outputData, null, 2)}</pre>
                                    </Box>
                                  </Box>
                                )}
                              </AccordionDetails>
                            </Accordion>
                          )}
                        </StepLabel>
                      </Step>
                    );
                  });
                })()}
              </Stepper>

              {/* State Variables During Execution */}
              {executionDetails.context && (
                <>
                  <Divider sx={{ my: 3 }} />
                  <Typography variant="h6" gutterBottom>
                    State Variables During Execution
                  </Typography>

                  {/* All Context Variables */}
                  <Card sx={{ mb: 2 }}>
                    <CardHeader
                      title={`All Context Variables (${Object.keys(executionDetails.context).length})`}
                      titleTypographyProps={{ variant: 'h6' }}
                    />
                    <CardContent>
                      <Grid container spacing={2}>
                        {Object.entries(executionDetails.context)
                          .sort(([a], [b]) => a.localeCompare(b))
                          .map(([key, value]) => {
                          // Determine variable category and color
                            let category = 'general';
                            let color = 'primary.main';

                            if (key.startsWith('form_') || key.includes('form')) {
                              category = 'form';
                              color = 'warning.main';
                            } else if (key.startsWith('execution_') || key.includes('execution')) {
                              category = 'execution';
                              color = 'info.main';
                            } else if (key.startsWith('flow_') || key.includes('flow')) {
                              category = 'flow';
                              color = 'secondary.main';
                            } else if (key.startsWith('api_') || key.includes('response') || key.includes('data')) {
                              category = 'api';
                              color = 'success.main';
                            }

                            return (
                              <Grid item xs={12} md={6} key={key}>
                                <Box sx={{
                                  border: '1px solid',
                                  borderColor: 'divider',
                                  borderRadius: 1,
                                  p: 2,
                                  backgroundColor: 'grey.50',
                                }}
                                >
                                  <Typography variant="subtitle2" sx={{ color, fontWeight: 'bold', mb: 1 }}>
                                    {key}
                                    <Chip
                                      label={category}
                                      size="small"
                                      variant="outlined"
                                      sx={{ ml: 1, fontSize: '0.7rem', height: '18px' }}
                                    />
                                  </Typography>
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      fontFamily: 'monospace',
                                      fontSize: '0.8rem',
                                      wordBreak: 'break-word',
                                      maxHeight: '150px',
                                      overflow: 'auto',
                                    }}
                                  >
                                    {typeof value === 'object'
                                      ? JSON.stringify(value, null, 2)
                                      : typeof value === 'string' && value.length > 100
                                        ? `${value.substring(0, 100)}...`
                                        : String(value)}
                                  </Typography>
                                </Box>
                              </Grid>
                            );
                          })}
                      </Grid>
                    </CardContent>
                  </Card>

                  {/* Execution-Specific Variables */}
                  <Card sx={{ mb: 2 }}>
                    <CardHeader
                      title="Execution Flow Variables"
                      titleTypographyProps={{ variant: 'h6' }}
                    />
                    <CardContent>
                      <Grid container spacing={2}>
                        {[
                          { key: 'execution_path', label: 'Execution Path', icon: 'timeline' },
                          { key: 'current_node', label: 'Current Node', icon: 'node' },
                          { key: 'flow_completed', label: 'Flow Completed', icon: 'check' },
                          { key: 'waiting_for_form_input', label: 'Waiting for Form', icon: 'form' },
                          { key: 'form_node_waiting', label: 'Form Node Waiting', icon: 'form' },
                          { key: 'nodes_executed', label: 'Nodes Executed', icon: 'count' },
                          { key: 'total_nodes', label: 'Total Nodes', icon: 'total' },
                          { key: 'execution_path_length', label: 'Path Length', icon: 'length' },
                        ].map(({ key, label, icon }) => {
                          const value = executionDetails.context[key];
                          if (value === undefined || value === null) return null;

                          return (
                            <Grid item xs={6} md={3} key={key}>
                              <Box sx={{
                                textAlign: 'center',
                                p: 2,
                                border: '1px solid',
                                borderColor: 'divider',
                                borderRadius: 1,
                                backgroundColor: 'background.paper',
                              }}
                              >
                                <Typography variant="h6" color="primary">
                                  {Array.isArray(value) ? value.length : value}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {label}
                                </Typography>
                              </Box>
                            </Grid>
                          );
                        })}
                      </Grid>
                    </CardContent>
                  </Card>

                  {/* API and Data Variables */}
                  <Card sx={{ mb: 2 }}>
                    <CardHeader
                      title="API & Data Variables"
                      titleTypographyProps={{ variant: 'h6' }}
                    />
                    <CardContent>
                      <Grid container spacing={2}>
                        {Object.entries(executionDetails.context).map(([key, value]) => {
                          if (!key.includes('response') && !key.includes('data') && !key.includes('result') && !key.startsWith('api_')) {
                            return null;
                          }

                          return (
                            <Grid item xs={12} md={6} key={key}>
                              <Box sx={{
                                border: '1px solid',
                                borderColor: 'success.light',
                                borderRadius: 1,
                                p: 2,
                                backgroundColor: 'success.50',
                              }}
                              >
                                <Typography variant="subtitle2" sx={{ color: 'success.main', fontWeight: 'bold', mb: 1 }}>
                                  {key}
                                </Typography>
                                <Typography
                                  variant="body2"
                                  sx={{
                                    fontFamily: 'monospace',
                                    fontSize: '0.8rem',
                                    wordBreak: 'break-word',
                                    maxHeight: '120px',
                                    overflow: 'auto',
                                  }}
                                >
                                  {typeof value === 'object'
                                    ? JSON.stringify(value, null, 2)
                                    : typeof value === 'string' && value.length > 80
                                      ? `${value.substring(0, 80)}...`
                                      : String(value)}
                                </Typography>
                              </Box>
                            </Grid>
                          );
                        })}
                      </Grid>
                    </CardContent>
                  </Card>

                  {/* Form-Related Variables */}
                  {Object.keys(executionDetails.context).some((key) => key.includes('form')) && (
                    <Card sx={{ mb: 2 }}>
                      <CardHeader
                        title="Form Variables"
                        titleTypographyProps={{ variant: 'h6' }}
                      />
                      <CardContent>
                        <Grid container spacing={2}>
                          {Object.entries(executionDetails.context).map(([key, value]) => {
                            if (!key.includes('form')) return null;

                            return (
                              <Grid item xs={12} md={6} key={key}>
                                <Box sx={{
                                  border: '1px solid',
                                  borderColor: 'warning.light',
                                  borderRadius: 1,
                                  p: 2,
                                  backgroundColor: 'warning.50',
                                }}
                                >
                                  <Typography variant="subtitle2" sx={{ color: 'warning.main', fontWeight: 'bold', mb: 1 }}>
                                    {key}
                                  </Typography>
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      fontFamily: 'monospace',
                                      fontSize: '0.8rem',
                                      wordBreak: 'break-word',
                                      maxHeight: '120px',
                                      overflow: 'auto',
                                    }}
                                  >
                                    {typeof value === 'object'
                                      ? JSON.stringify(value, null, 2)
                                      : String(value)}
                                  </Typography>
                                </Box>
                              </Grid>
                            );
                          })}
                        </Grid>
                      </CardContent>
                    </Card>
                  )}

                  {/* Flow Global State Variables */}
                  {executionDetails.flow_definition?.globalState?.states && (
                    <Card sx={{ mb: 2 }}>
                      <CardHeader
                        title={`Flow Global State Variables (${executionDetails.flow_definition.globalState.states.length})`}
                        titleTypographyProps={{ variant: 'h6' }}
                        subheader="Variables defined in flow configuration"
                      />
                      <CardContent>
                        <Grid container spacing={2}>
                          {executionDetails.flow_definition.globalState.states.map((stateVar) => {
                            const currentValue = executionDetails.context[stateVar.name];
                            const isSet = currentValue !== undefined && currentValue !== null && currentValue !== stateVar.defaultValue;

                            return (
                              <Grid item xs={12} md={6} key={stateVar.name}>
                                <Box sx={{
                                  border: '1px solid',
                                  borderColor: isSet ? 'success.light' : 'grey.300',
                                  borderRadius: 1,
                                  p: 2,
                                  backgroundColor: isSet ? 'success.50' : 'grey.50',
                                  opacity: isSet ? 1 : 0.7,
                                }}
                                >
                                  <Box sx={{
                                    display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1,
                                  }}
                                  >
                                    <Typography
                                      variant="subtitle2"
                                      sx={{
                                        color: isSet ? 'success.main' : 'text.secondary',
                                        fontWeight: 'bold',
                                      }}
                                    >
                                      {stateVar.name}
                                    </Typography>
                                    <Chip
                                      label={isSet ? 'Set' : 'Default'}
                                      size="small"
                                      color={isSet ? 'success' : 'default'}
                                      variant={isSet ? 'filled' : 'outlined'}
                                    />
                                  </Box>
                                  <Typography variant="caption" color="text.secondary" sx={{ mb: 1 }}>
                                    Type:
                                    {' '}
                                    {stateVar.type}
                                    {' '}
                                    | Default:
                                    {' '}
                                    {stateVar.defaultValue || 'null'}
                                  </Typography>
                                  {isSet && (
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        fontFamily: 'monospace',
                                        fontSize: '0.8rem',
                                        wordBreak: 'break-word',
                                        maxHeight: '100px',
                                        overflow: 'auto',
                                        backgroundColor: 'white',
                                        p: 1,
                                        borderRadius: 0.5,
                                        border: '1px solid',
                                        borderColor: 'success.light',
                                      }}
                                    >
                                      {typeof currentValue === 'object'
                                        ? JSON.stringify(currentValue, null, 2)
                                        : typeof currentValue === 'string' && currentValue.length > 60
                                          ? `${currentValue.substring(0, 60)}...`
                                          : String(currentValue)}
                                    </Typography>
                                  )}
                                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                                    Used by
                                    {' '}
                                    {stateVar.nodes?.length || 0}
                                    {' '}
                                    nodes
                                  </Typography>
                                </Box>
                              </Grid>
                            );
                          })}
                        </Grid>
                      </CardContent>
                    </Card>
                  )}

                  {/* Original filtered state variables for backward compatibility */}
                  <Card>
                    <CardHeader
                      title="Filtered State Variables (Legacy)"
                      titleTypographyProps={{ variant: 'h6' }}
                      subheader="Variables that were actively set during execution"
                    />
                    <CardContent>
                      <Stack spacing={2}>
                        {Object.entries(executionDetails.context).map(([key, value]) => {
                          // Filter out system-level variables but keep important state variables
                          if (key.startsWith('form_')
                              || key.startsWith('execution_')
                              || key.startsWith('flow_')
                              || key === 'waiting_for_form_input'
                              || key === 'form_node_waiting'
                              || key === 'form_data'
                              || key === 'form_message'
                              || key === 'current_form_message'
                              || key === 'execution_settings'
                              || key === 'execution_log'
                              || key === 'execution_path'
                              || key === 'control_flow'
                              || key === 'execution_metrics') {
                            return null;
                          }

                          return (
                            <Box key={key}>
                              <Typography variant="body2" fontWeight="bold" color="primary">
                                {key}
                                :
                              </Typography>
                              <Typography
                                variant="body2"
                                sx={{
                                  ml: 2,
                                  fontFamily: 'monospace',
                                  fontSize: '0.9rem',
                                  backgroundColor: 'grey.50',
                                  padding: '8px',
                                  borderRadius: '4px',
                                  maxHeight: '200px',
                                  overflow: 'auto',
                                }}
                              >
                                {typeof value === 'string' && value.length > 200
                                  ? `${value.substring(0, 200)}...`
                                  : JSON.stringify(value, null, 2)}
                              </Typography>
                            </Box>
                          );
                        })}

                      </Stack>
                    </CardContent>
                  </Card>
                </>
              )}

              {/* Final Result */}
              {executionDetails.result && (
                <>
                  <Divider sx={{ my: 3 }} />
                  <Typography variant="h6" gutterBottom>
                    Final Result
                  </Typography>
                  <Card>
                    <CardContent>
                      <pre style={{
                        fontSize: '0.9rem',
                        overflow: 'auto',
                        maxHeight: '300px',
                        fontFamily: 'monospace',
                        backgroundColor: 'grey.50',
                        padding: '16px',
                        borderRadius: '4px',
                      }}
                      >
                        {JSON.stringify(executionDetails.result, null, 2)}
                      </pre>
                    </CardContent>
                  </Card>
                </>
              )}

              {/* Error Information */}
              {executionDetails.error && (
                <>
                  <Divider sx={{ my: 3 }} />
                  <Typography variant="h6" gutterBottom color="error">
                    Error Information
                  </Typography>
                  <Alert severity="error">
                    <Typography variant="body2" fontWeight="bold">Error:</Typography>
                    <Typography variant="body2">{executionDetails.error}</Typography>
                  </Alert>
                </>
              )}

              {/* Execution Log */}
              {executionDetails.execution_log && executionDetails.execution_log.length > 0 && (
                <>
                  <Divider sx={{ my: 3 }} />
                  <Typography variant="h6" gutterBottom>
                    Execution Log (
                    {executionDetails.execution_log.length}
                    {' '}
                    entries)
                  </Typography>
                  <Card>
                    <CardContent>
                      <List dense>
                        {executionDetails.execution_log.map((log, index) => (
                          <ListItem key={index}>
                            <ListItemText
                              primary={log}
                              sx={{
                                '& .MuiListItemText-primary': {
                                  fontFamily: 'monospace',
                                  fontSize: '0.8rem',
                                },
                              }}
                            />
                          </ListItem>
                        ))}
                      </List>
                    </CardContent>
                  </Card>
                </>
              )}

              {/* Enhanced Performance Data */}
              {executionDetails.metrics?.performance_metrics && Object.keys(executionDetails.metrics.performance_metrics).length > 0 && (
                <>
                  <Divider sx={{ my: 3 }} />
                  <Typography variant="h6" gutterBottom>
                    Performance Metrics
                  </Typography>
                  <Card>
                    <CardContent>
                      <Grid container spacing={2}>
                        {Object.entries(executionDetails.metrics.performance_metrics).map(([key, value]) => (
                          <Grid item xs={6} md={3} key={key}>
                            <Typography variant="caption" color="text.secondary">
                              {key.replace(/_/g, ' ').toUpperCase()}
                            </Typography>
                            <Typography variant="body2">
                              {typeof value === 'number' ? value.toFixed(2) : String(value)}
                            </Typography>
                          </Grid>
                        ))}
                      </Grid>
                    </CardContent>
                  </Card>
                </>
              )}

              {/* Conditional Flow Data */}
              {executionDetails.metrics?.conditional_branches && executionDetails.metrics.conditional_branches.length > 0 && (
                <>
                  <Divider sx={{ my: 3 }} />
                  <Typography variant="h6" gutterBottom>
                    Conditional Flow Data
                  </Typography>
                  <Card>
                    <CardContent>
                      <Stack spacing={2}>
                        {executionDetails.metrics.conditional_branches.map((branch, index) => (
                          <Paper key={index} variant="outlined" sx={{ p: 2 }}>
                            <Typography variant="subtitle2" gutterBottom>
                              Branch
                              {' '}
                              {index + 1}
                              :
                              {' '}
                              {branch.from_node}
                              {' '}
                              →
                              {' '}
                              {branch.selected_path}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Available paths:
                              {' '}
                              {branch.available_paths.join(', ')}
                            </Typography>
                          </Paper>
                        ))}
                      </Stack>
                    </CardContent>
                  </Card>
                </>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDetails}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Debug Interface Dialog */}
      <Dialog
        open={debugDialogOpen}
        onClose={handleCloseDebug}
        maxWidth="xl"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{
          sx: { minHeight: '80vh' },
        }}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <DebugIcon />
            <Typography variant="h6">
              Debug Interface - Execution:
              {' '}
              {debugExecution?.execution_id}
            </Typography>
            {debugData?.status && (
              <Chip
                label={debugData.status.toUpperCase()}
                color={debugData.status === 'paused' ? 'warning' : 'info'}
                size="small"
                sx={{ ml: 2 }}
              />
            )}
          </Box>
        </DialogTitle>
        <DialogContent>
          {debugData ? (
            <Box sx={{ p: 2 }}>
              {/* Debug Controls */}
              <Card sx={{ mb: 3 }}>
                <CardHeader title="Debug Controls" />
                <CardContent>
                  <Stack spacing={2}>
                    {/* Primary Controls */}
                    <Stack direction="row" spacing={2} flexWrap="wrap">
                      <Button
                        variant="contained"
                        startIcon={<ResumeIcon />}
                        onClick={() => handleResumeExecution(debugData.execution_id)}
                        color="success"
                        size="small"
                      >
                        Resume Execution
                      </Button>
                      <Button
                        variant="outlined"
                        startIcon={<SkipNextIcon />}
                        onClick={() => handleStepExecution(debugData.execution_id)}
                        size="small"
                      >
                        Step to Next Node
                      </Button>
                      <Button
                        variant="outlined"
                        startIcon={<PlayIcon />}
                        onClick={() => handleRunToEnd(debugData.execution_id)}
                        size="small"
                      >
                        Run to End
                      </Button>
                      <Button
                        variant="outlined"
                        startIcon={<StopIcon />}
                        onClick={() => handleStopExecution(debugData.execution_id)}
                        color="error"
                        size="small"
                      >
                        Stop Execution
                      </Button>
                    </Stack>

                    {/* Advanced Controls */}
                    <Divider sx={{ my: 1 }} />
                    <Typography variant="subtitle2" color="text.secondary">
                      Advanced Controls
                    </Typography>
                    <Stack direction="row" spacing={1} flexWrap="wrap">
                      <Button
                        variant="text"
                        size="small"
                        startIcon={<MemoryIcon />}
                        onClick={() => handleInspectMemory(debugData.execution_id)}
                      >
                        Inspect Memory
                      </Button>
                      <Button
                        variant="text"
                        size="small"
                        startIcon={<TimelineIcon />}
                        onClick={() => handleViewExecutionGraph(debugData.execution_id)}
                      >
                        Execution Graph
                      </Button>
                      <Button
                        variant="text"
                        size="small"
                        startIcon={<AssessmentIcon />}
                        onClick={() => handleGenerateReport(debugData.execution_id)}
                      >
                        Debug Report
                      </Button>
                      <Button
                        variant="text"
                        size="small"
                        startIcon={<DownloadIcon />}
                        onClick={() => handleExportDebugData(debugData.execution_id)}
                      >
                        Export Data
                      </Button>
                    </Stack>

                    {/* Quick Actions */}
                    <Divider sx={{ my: 1 }} />
                    <Typography variant="subtitle2" color="text.secondary">
                      Quick Actions
                    </Typography>
                    <Stack direction="row" spacing={1} flexWrap="wrap">
                      <Button
                        variant="text"
                        size="small"
                        color="warning"
                        onClick={() => handleResetToCheckpoint(debugData.execution_id)}
                      >
                        Reset to Last Checkpoint
                      </Button>
                      <Button
                        variant="text"
                        size="small"
                        color="info"
                        onClick={() => handleAddBreakpoint(debugData.execution_id)}
                      >
                        Add Breakpoint
                      </Button>
                      <Button
                        variant="text"
                        size="small"
                        color="secondary"
                        onClick={() => handleClearBreakpoints(debugData.execution_id)}
                      >
                        Clear All Breakpoints
                      </Button>
                    </Stack>
                  </Stack>
                </CardContent>
              </Card>

              {/* Current State Inspector & Editor */}
              <Card sx={{ mb: 3 }}>
                <CardHeader
                  title="Current State"
                  action={(
                    <Button
                      size="small"
                      onClick={() => setDebugData((prev) => ({ ...prev, editingVariables: !prev.editingVariables }))}
                    >
                      {debugData.editingVariables ? 'View Mode' : 'Edit Mode'}
                    </Button>
                  )}
                />
                <CardContent>
                  <Typography variant="subtitle2" gutterBottom>
                    Variables (
                    {Object.keys(debugData.variables || {}).length}
                    )
                  </Typography>
                  <Box sx={{ maxHeight: 300, overflow: 'auto' }}>
                    {debugData.editingVariables ? (
                      <VariableEditor
                        variables={debugData.variables || {}}
                        onSave={(newVariables) => handleModifyState(debugData.execution_id, { variables: newVariables })}
                      />
                    ) : Object.keys(debugData.variables || {}).length > 0 ? (
                      <pre style={{ fontSize: '0.8rem', margin: 0 }}>
                        {JSON.stringify(debugData.variables, null, 2)}
                      </pre>
                    ) : (
                      <Typography color="text.secondary">No variables available</Typography>
                    )}
                  </Box>
                </CardContent>
              </Card>

              {/* Execution Path */}
              <Card sx={{ mb: 3 }}>
                <CardHeader title="Execution Path" />
                <CardContent>
                  <Box sx={{ maxHeight: 300, overflow: 'auto' }}>
                    {debugData.execution_path && debugData.execution_path.length > 0 ? (
                      <Stepper orientation="vertical" sx={{ mb: 2 }}>
                        {debugData.execution_path.map((nodeId, index) => (
                          <Step key={index} active={index === debugData.execution_path.length - 1}>
                            <StepLabel>
                              {typeof nodeId === 'string' ? nodeId : nodeId.node_id || `Node ${index + 1}`}
                            </StepLabel>
                            {typeof nodeId === 'object' && nodeId.status && (
                              <StepContent>
                                <Typography variant="body2" color="text.secondary">
                                  Status:
                                  {' '}
                                  {nodeId.status}
                                </Typography>
                                {nodeId.timestamp && (
                                  <Typography variant="body2" color="text.secondary">
                                    Time:
                                    {' '}
                                    {new Date(nodeId.timestamp).toLocaleTimeString()}
                                  </Typography>
                                )}
                              </StepContent>
                            )}
                          </Step>
                        ))}
                      </Stepper>
                    ) : (
                      <Typography color="text.secondary">No execution path available</Typography>
                    )}
                  </Box>
                </CardContent>
              </Card>

              {/* Debug Sessions */}
              {debugData.debug_sessions && debugData.debug_sessions.length > 0 && (
                <Card sx={{ mb: 3 }}>
                  <CardHeader title="Debug Sessions" />
                  <CardContent>
                    <Box sx={{ maxHeight: 200, overflow: 'auto' }}>
                      {debugData.debug_sessions.map((session, index) => (
                        <Box
                          key={index}
                          sx={{
                            mb: 2, p: 2, border: 1, borderColor: 'divider', borderRadius: 1,
                          }}
                        >
                          <Typography variant="subtitle2">
                            Session
                            {' '}
                            {index + 1}
                            {' '}
                            -
                            {' '}
                            {session.debug_mode || 'Unknown Mode'}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Node:
                            {' '}
                            {session.node_id || 'Unknown'}
                          </Typography>
                          {session.debug_info && (
                            <Box sx={{ mt: 1 }}>
                              <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                                {JSON.stringify(session.debug_info, null, 2)}
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      ))}
                    </Box>
                  </CardContent>
                </Card>
              )}

              {/* Breakpoint Manager */}
              <Card sx={{ mb: 3 }}>
                <CardHeader
                  title="Breakpoint Manager"
                  action={(
                    <Button
                      size="small"
                      startIcon={<AddIcon />}
                      onClick={() => handleAddBreakpoint(debugData.execution_id)}
                    >
                      Add Breakpoint
                    </Button>
                  )}
                />
                <CardContent>
                  <Box sx={{ maxHeight: 200, overflow: 'auto' }}>
                    {debugData.breakpoints && debugData.breakpoints.length > 0 ? (
                      <List dense>
                        {debugData.breakpoints.map((breakpoint, index) => (
                          <ListItem
                            key={index}
                            sx={{
                              border: 1, borderColor: 'divider', borderRadius: 1, mb: 1,
                            }}
                          >
                            <ListItemIcon>
                              <WarningIcon color="error" />
                            </ListItemIcon>
                            <ListItemText
                              primary={`Node: ${breakpoint.node_id || breakpoint.nodeId}`}
                              secondary={`Condition: ${breakpoint.condition || 'No condition'}`}
                            />
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleRemoveBreakpoint(debugData.execution_id, breakpoint.node_id || breakpoint.nodeId)}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </ListItem>
                        ))}
                      </List>
                    ) : (
                      <Typography color="text.secondary">No breakpoints set</Typography>
                    )}
                  </Box>
                  {debugData.breakpoints && debugData.breakpoints.length > 0 && (
                    <Box sx={{ mt: 2 }}>
                      <Button
                        variant="outlined"
                        size="small"
                        color="secondary"
                        onClick={() => handleClearBreakpoints(debugData.execution_id)}
                        startIcon={<CleaningServicesIcon />}
                      >
                        Clear All Breakpoints
                      </Button>
                    </Box>
                  )}
                </CardContent>
              </Card>

              {/* Logs */}
              <Card>
                <CardHeader title="Execution Logs" />
                <CardContent>
                  <Box sx={{ maxHeight: 200, overflow: 'auto' }}>
                    {debugData.logs && debugData.logs.length > 0 ? (
                      <List dense>
                        {debugData.logs.map((log, index) => (
                          <ListItem key={index}>
                            <ListItemText
                              primary={log}
                              sx={{
                                '& .MuiListItemText-primary': {
                                  fontFamily: 'monospace',
                                  fontSize: '0.8rem',
                                },
                              }}
                            />
                          </ListItem>
                        ))}
                      </List>
                    ) : (
                      <Typography color="text.secondary">No logs available</Typography>
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
              <Typography sx={{ ml: 2 }}>Loading debug data...</Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDebug}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Enhanced Error Recovery Modal */}
      <ErrorRecoveryModal
        open={errorRecoveryState.showModal}
        onClose={() => setErrorRecoveryState((prev) => ({ ...prev, showModal: false }))}
        executionId={errorRecoveryState.selectedExecution}
        error={errorRecoveryState.error}
        executionData={errorRecoveryState.executionData}
        onRecoverySuccess={(result) => handleRecoverySuccess(errorRecoveryState.selectedExecution, result)}
        onRetry={() => handleRetryExecution(errorRecoveryState.selectedExecution, errorRecoveryState.executionData)}
      />

      {/* Execution Replay Modal */}
      <Dialog open={replayDialogOpen} onClose={handleCloseReplay} maxWidth="lg" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PlayArrowIcon color="primary" />
            <Typography variant="h6">Execution Replay</Typography>
            <Chip
              label={currentReplayExecution?.execution_id || 'N/A'}
              size="small"
              color="primary"
              variant="outlined"
            />
          </Box>
        </DialogTitle>
        <DialogContent>
          {currentReplayExecution ? (
            <Box sx={{ mt: 2 }}>
              {/* Replay Controls */}
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Box sx={{
                    display: 'flex', alignItems: 'center', gap: 2, mb: 2,
                  }}
                  >
                    <IconButton
                      onClick={handleReplayPlayPause}
                      color="primary"
                      disabled={!currentReplayExecution.execution_steps?.length}
                    >
                      {isReplayPlaying ? <PauseIcon /> : <PlayArrowIcon />}
                    </IconButton>
                    <IconButton
                      onClick={handleReplayStop}
                      color="secondary"
                    >
                      <StopIcon />
                    </IconButton>
                    <IconButton
                      onClick={handleReplayReset}
                      color="default"
                    >
                      <ReplayIcon />
                    </IconButton>
                    <Typography variant="body2" sx={{ ml: 2 }}>
                      Speed:
                      {' '}
                      {replaySpeed}
                      x
                    </Typography>
                    <Slider
                      value={replaySpeed}
                      onChange={(_, value) => setReplaySpeed(value)}
                      min={0.25}
                      max={4}
                      step={0.25}
                      sx={{ width: 100, ml: 2 }}
                    />
                  </Box>

                  {/* Progress Bar */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Typography variant="body2" sx={{ minWidth: 60 }}>
                      Step
                      {' '}
                      {currentReplayStep + 1}
                      {' '}
                      /
                      {' '}
                      {currentReplayExecution.execution_steps?.length || 0}
                    </Typography>
                    <LinearProgress
                      variant="determinate"
                      value={currentReplayExecution.execution_steps?.length
                        ? ((currentReplayStep + 1) / currentReplayExecution.execution_steps.length) * 100 : 0}
                      sx={{ flexGrow: 1 }}
                    />
                    <Typography variant="body2" sx={{ minWidth: 80 }}>
                      {formatDuration(replayElapsedTime)}
                      {' '}
                      /
                      {formatDuration(currentReplayExecution.execution_time || 0)}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>

              {/* Replay Timeline */}
              <Card>
                <CardHeader
                  title="Execution Timeline"
                  subheader="Step-by-step replay of the execution"
                />
                <CardContent>
                  <Box>
                    {currentReplayExecution.execution_steps?.map((step, index) => (
                      <Box
                        key={index}
                        sx={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          mb: 2,
                          opacity: index <= currentReplayStep ? 1 : 0.5,
                        }}
                      >
                        <Box sx={{
                          display: 'flex', flexDirection: 'column', alignItems: 'center', mr: 2,
                        }}
                        >
                          <Avatar
                            sx={{
                              bgcolor: index < currentReplayStep ? 'primary.main'
                                : index === currentReplayStep ? 'secondary.main' : 'grey.300',
                              mb: 1,
                            }}
                          >
                            {getNodeIcon(step.node_type)}
                          </Avatar>
                          {index < currentReplayExecution.execution_steps.length - 1 && (
                            <Box
                              sx={{
                                width: 2,
                                height: 40,
                                bgcolor: index < currentReplayStep ? 'primary.main' : 'grey.300',
                              }}
                            />
                          )}
                        </Box>
                        <Box sx={{ flex: 1 }}>
                          <Card
                            sx={{
                              mb: 2,
                              border: index === currentReplayStep ? 2 : 1,
                              borderColor: index === currentReplayStep ? 'primary.main' : 'grey.300',
                            }}
                          >
                            <CardContent>
                              <Box sx={{
                                display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1,
                              }}
                              >
                                <Typography variant="h6">
                                  {step.node_id}
                                  {' '}
                                  (
                                  {step.node_type}
                                  )
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                  <Chip
                                    label={step.status || 'pending'}
                                    size="small"
                                    color={step.status === 'completed' ? 'success'
                                      : step.status === 'failed' ? 'error' : 'default'}
                                  />
                                  {step.duration && (
                                    <Chip
                                      label={`${step.duration}ms`}
                                      size="small"
                                      variant="outlined"
                                    />
                                  )}
                                </Box>
                              </Box>

                              {step.timestamp && (
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                  {new Date(step.timestamp).toLocaleString()}
                                </Typography>
                              )}

                              {step.error && (
                                <Alert severity="error" sx={{ mb: 2 }}>
                                  <Typography variant="body2">{step.error}</Typography>
                                </Alert>
                              )}

                              {/* Input Data */}
                              {step.input_data && Object.keys(step.input_data).length > 0 && (
                                <Accordion>
                                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                    <Typography variant="subtitle2">Input Data</Typography>
                                  </AccordionSummary>
                                  <AccordionDetails>
                                    <pre style={{
                                      fontSize: '12px',
                                      background: '#f5f5f5',
                                      padding: '12px',
                                      borderRadius: '4px',
                                      overflow: 'auto',
                                      maxHeight: '200px',
                                    }}
                                    >
                                      {JSON.stringify(step.input_data, null, 2)}
                                    </pre>
                                  </AccordionDetails>
                                </Accordion>
                              )}

                              {/* Output Data */}
                              {step.output_data && Object.keys(step.output_data).length > 0 && (
                                <Accordion>
                                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                    <Typography variant="subtitle2">Output Data</Typography>
                                  </AccordionSummary>
                                  <AccordionDetails>
                                    <pre style={{
                                      fontSize: '12px',
                                      background: '#f5f5f5',
                                      padding: '12px',
                                      borderRadius: '4px',
                                      overflow: 'auto',
                                      maxHeight: '200px',
                                    }}
                                    >
                                      {JSON.stringify(step.output_data, null, 2)}
                                    </pre>
                                  </AccordionDetails>
                                </Accordion>
                              )}
                            </CardContent>
                          </Card>
                        </Box>
                      </Box>
                    ))}
                  </Box>
                </CardContent>
              </Card>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
              <Typography sx={{ ml: 2 }}>Loading execution data...</Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseReplay}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Variable Inspector Modal */}
      <Dialog open={variableInspectorOpen} onClose={handleCloseVariableInspector} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <VisibilityIcon color="primary" />
            <Typography variant="h6">Variable Inspector</Typography>
            <Chip
              label={currentInspectedExecution?.execution_id || 'N/A'}
              size="small"
              color="primary"
              variant="outlined"
            />
          </Box>
        </DialogTitle>
        <DialogContent>
          {currentInspectedExecution ? (
            <Box sx={{ mt: 2 }}>
              {/* Variable Search */}
              <TextField
                fullWidth
                placeholder="Search variables..."
                value={variableSearchTerm}
                onChange={(e) => setVariableSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
                sx={{ mb: 3 }}
              />

              {/* Variable Categories */}
              <Box sx={{
                display: 'flex', gap: 1, mb: 3, flexWrap: 'wrap',
              }}
              >
                {variableCategories.map((category) => (
                  <Chip
                    key={category}
                    label={category}
                    onClick={() => setSelectedVariableCategory(
                      selectedVariableCategory === category ? null : category,
                    )}
                    color={selectedVariableCategory === category ? 'primary' : 'default'}
                    variant={selectedVariableCategory === category ? 'filled' : 'outlined'}
                  />
                ))}
              </Box>

              {/* Variables List */}
              <Grid container spacing={2}>
                {filteredVariables.map((variable, index) => (
                  <Grid item xs={12} sm={6} md={4} key={index}>
                    <Card
                      sx={{
                        height: '100%',
                        cursor: 'pointer',
                        '&:hover': { boxShadow: 3 },
                      }}
                      onClick={() => setSelectedVariable(variable)}
                    >
                      <CardContent>
                        <Typography variant="h6" noWrap>
                          {variable.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                          {variable.type}
                        </Typography>
                        <Typography variant="body2" noWrap>
                          {typeof variable.value === 'object'
                            ? `${JSON.stringify(variable.value).substring(0, 50)}...`
                            : String(variable.value).substring(0, 50)}
                        </Typography>
                        {variable.lastModified && (
                          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                            Modified:
                            {' '}
                            {new Date(variable.lastModified).toLocaleString()}
                          </Typography>
                        )}
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>

              {/* Variable Detail Panel */}
              {selectedVariable && (
                <Card sx={{ mt: 3 }}>
                  <CardHeader
                    title={`Variable: ${selectedVariable.name}`}
                    subheader={`Type: ${selectedVariable.type}`}
                  />
                  <CardContent>
                    <Accordion defaultExpanded>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography>Current Value</Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        <pre style={{
                          fontSize: '12px',
                          background: '#f5f5f5',
                          padding: '12px',
                          borderRadius: '4px',
                          overflow: 'auto',
                          maxHeight: '300px',
                        }}
                        >
                          {JSON.stringify(selectedVariable.value, null, 2)}
                        </pre>
                      </AccordionDetails>
                    </Accordion>

                    <Accordion>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography>Variable History</Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        {selectedVariable.history?.length > 0 ? (
                          <Timeline>
                            {selectedVariable.history.map((entry, index) => (
                              <TimelineItem key={index}>
                                <TimelineSeparator>
                                  <TimelineDot color="primary" />
                                  {index < selectedVariable.history.length - 1 && <TimelineConnector />}
                                </TimelineSeparator>
                                <TimelineContent>
                                  <Typography variant="body2">
                                    {new Date(entry.timestamp).toLocaleString()}
                                  </Typography>
                                  <pre style={{
                                    fontSize: '10px',
                                    background: '#f5f5f5',
                                    padding: '8px',
                                    borderRadius: '4px',
                                    overflow: 'auto',
                                    maxHeight: '150px',
                                    marginTop: '8px',
                                  }}
                                  >
                                    {JSON.stringify(entry.value, null, 2)}
                                  </pre>
                                </TimelineContent>
                              </TimelineItem>
                            ))}
                          </Timeline>
                        ) : (
                          <Typography color="text.secondary">
                            No history available for this variable
                          </Typography>
                        )}
                      </AccordionDetails>
                    </Accordion>

                    <Accordion>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography>Variable Metadata</Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        <Grid container spacing={2}>
                          <Grid item xs={6}>
                            <Typography variant="body2">
                              <strong>Scope:</strong>
                              {' '}
                              {selectedVariable.scope || 'global'}
                            </Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography variant="body2">
                              <strong>Mutable:</strong>
                              {' '}
                              {selectedVariable.mutable ? 'Yes' : 'No'}
                            </Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography variant="body2">
                              <strong>Size:</strong>
                              {' '}
                              {JSON.stringify(selectedVariable.value).length}
                              {' '}
                              bytes
                            </Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography variant="body2">
                              <strong>Last Modified:</strong>
                              {' '}
                              {selectedVariable.lastModified
                                ? new Date(selectedVariable.lastModified).toLocaleString() : 'Unknown'}
                            </Typography>
                          </Grid>
                        </Grid>
                      </AccordionDetails>
                    </Accordion>
                  </CardContent>
                </Card>
              )}
            </Box>
          ) : (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
              <Typography sx={{ ml: 2 }}>Loading execution data...</Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseVariableInspector}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default FlowExecutionTracker;
